/**
 * BoardRenderer - SVG Board Construction, Hazard Overlays, Fancy Tooltips & Legend
 * Encapsulates SVG board generation for Serpentine (121/61/31) and Clash (481/361/241),
 * obstacle overlays, hover tooltips, and the multi-brand interactive legend.
 */

import { BOARD_CFG, BOARD_CFG_481, getClashBoardCfg, getTrackXY as defaultGetTrackXY } from './BoardGeometry.js';
import { ObstacleGeometry, getClashDefaults } from './ObstacleGeometry.js';
import { Board3DCatalog } from './Board3DCatalog.js';
import { BoardRenderer3D } from './BoardRenderer3D.js';
import { AvatarManager } from '../ui/AvatarManager.js';
import { Room3DLoadingOverlay } from '../ui/Room3DLoadingOverlay.js';

export class BoardRenderer {
  /**
   * Computes the cache key string for board redraw invalidation.
   * @param {Object} settings
   * @param {Array<string>} activeTracks
   * @param {string} brand
   * @returns {string}
   */
  static getBoardSignature(settings = {}, activeTracks = [], brand = '') {
    const tracks = (Array.isArray(activeTracks) ? activeTracks : []).join(',');
    let t = settings.target || 121;
    if (brand === 'clash' && ![241, 361, 481].includes(t)) {
      t = 481;
    }
    const obs = [
      settings.optNoPassingLanes,
      settings.optJump,
      settings.optJail,
      settings.optBar,
      settings.optTimeout,
      settings.optDetour,
      settings.optRoundAbout,
      settings.optSigns,
      settings.optStopSign,
      settings.optSkunkMarkers,
      settings.optYouDrink,
      settings.optGuysDrink,
      settings.optGalsDrink,
      settings.optLeftDrink,
      settings.optRightDrink,
      settings.optEveryoneDrink,
      settings.optEveryoneButYouDrink,
      settings.optPickWhoDrinks
    ].map(Boolean).join('');
    const finish = settings.boardFinish || 'maple';
    const skin = settings.pegSkin || 'brass';
    const viewMode = settings.boardViewMode || '2d';
    const board3D = settings.selected3DBoardId || '';
    return `${brand}|${t}|${tracks}|${obs}|${finish}|${skin}|${viewMode}|${board3D}`;
  }

  /**
   * Updates top scoreboard chips for active tracks.
   * @param {Document} doc
   * @param {Array<string>} tracks
   * @param {Function} pName
   * @param {Object} settings
   */
  static updateScoreChips(doc, tracks = [], pName = (w => w), settings = {}) {
    if (!doc) return;
    const topEl = doc.getElementById('boardTopChips');
    if (!topEl) return;
    const is2P = settings && settings.playerCount === 2;
    const currentChips = topEl.querySelectorAll('.score-chip');
    const chipIds = Array.from(currentChips).map(c => c.id);
    const expectedIds = tracks.map(k => `chip-${k}`);
    const needsRebuild = currentChips.length !== tracks.length || chipIds.some((id, idx) => id !== expectedIds[idx]);

    const isSeatHuman = (w) => {
      if (typeof window !== 'undefined' && typeof window.isSeatHuman === 'function') {
        return window.isSeatHuman(w);
      }
      if (settings && settings.seats) {
        const s = settings.seats.find(x => x && x.id === w);
        if (s) return s.type === 'human';
      }
      return w === 'p1' || Boolean(settings && settings.isHvH);
    };

    const isSeatOnline = (w) => {
      if (typeof window !== 'undefined' && typeof window.isSeatOnline === 'function') {
        return window.isSeatOnline(w);
      }
      if (settings && settings.seats) {
        const s = settings.seats.find(x => x && x.id === w);
        if (s && s.online !== undefined) return Boolean(s.online);
      }
      return true;
    };

    const isMultiHuman = () => {
      if (typeof window !== 'undefined' && typeof window.isMultiHuman === 'function') {
        return window.isMultiHuman();
      }
      if (settings && settings.seats) {
        return settings.seats.filter(s => s && s.type === 'human').length >= 2;
      }
      return Boolean(settings && (settings.opp === 'online' || settings.isHvH));
    };

    const multiHuman = isMultiHuman();

    const getIndicatorHtml = (k) => {
      if (!multiHuman || !isSeatHuman(k)) return '';
      const online = isSeatOnline(k);
      return `<span class="online-indicator ${online ? 'online' : 'offline'}" title="${online ? 'Online' : 'Offline'}"></span>`;
    };

    if (needsRebuild) {
      const chipsHtml = tracks.map(key => {
        const label = pName(key);
        const cls = (key === 'p1' && is2P) ? 'you' : ((key === 'p2' && is2P) ? 'cpu' : key);
        const youCpuId = (key === 'p1' && is2P) ? 'you' : ((key === 'p2' && is2P) ? 'cpu' : key);
        const ind = getIndicatorHtml(key);
        const isYou = (key === 'p1' || key === 'you');
        let avatarBadgeHtml = '';
        if (isYou) {
          const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
          if (avMgr && typeof avMgr.getSelectedAvatarId === 'function') {
            avatarBadgeHtml = `<span class="score-chip-avatar" id="scoreChipAvatarYou">${avMgr.renderAvatarBadge(avMgr.getSelectedAvatarId(), { size: 20, showRing: false })}</span>`;
          }
        }
        return `<span class="score-chip ${cls}" id="chip-${key}">${avatarBadgeHtml}<span id="${youCpuId}Label">${ind}${label}</span> <span id="${youCpuId}Score">0</span></span>`;
      }).join('');

      const nonChipEls = Array.from(topEl.children).filter(el => !el.classList.contains('score-chip'));
      topEl.querySelectorAll('.score-chip').forEach(el => el.remove());
      const temp = doc.createElement('div');
      temp.innerHTML = chipsHtml;
      while (temp.firstChild) {
        if (nonChipEls[0]) {
          topEl.insertBefore(temp.firstChild, nonChipEls[0]);
        } else {
          topEl.appendChild(temp.firstChild);
        }
      }
    } else {
      tracks.forEach(key => {
        const youCpuId = (key === 'p1' && is2P) ? 'you' : ((key === 'p2' && is2P) ? 'cpu' : key);
        const lbl = doc.getElementById(`${youCpuId}Label`);
        if (lbl) {
          const ind = getIndicatorHtml(key);
          lbl.innerHTML = `${ind}${pName(key)}`;
        }
        const isYou = (key === 'p1' || key === 'you');
        if (isYou) {
          const avEl = doc.getElementById('scoreChipAvatarYou');
          if (avEl) {
            const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
            if (avMgr && typeof avMgr.getSelectedAvatarId === 'function') {
              avEl.innerHTML = avMgr.renderAvatarBadge(avMgr.getSelectedAvatarId(), { size: 20, showRing: false });
            }
          }
        }
      });
    }
  }

  /**
   * Resolves tooltip data for an element hovered on the cribbage board.
   * @param {Element} target
   * @param {Object} context
   * @returns {Object|null}
   */
  static getBoardTooltipData(target, context = {}) {
    if (!target) return null;
    const {
      state,
      settings,
      pegState,
      brand,
      getStreetOrTurnName,
      getSignConfigs,
      getStopSignConfigs,
      getJailConfigs,
      getBarConfigs,
      getTimeoutConfigs,
      getJumpConfigs,
      getDetourConfigs,
      getRoundAboutConfigs,
      getNoPassingLanes
    } = context;

    // 1. Pegs
    const pegEl = target.closest ? target.closest('[data-peg-who], .peg-group') : null;
    if (pegEl && (pegEl.dataset?.pegWho || (pegEl.id && (pegEl.id.includes('p1') || pegEl.id.includes('p2'))))) {
      const who = pegEl.dataset?.pegWho || (pegEl.id && pegEl.id.includes('p1') ? 'p1' : 'p2');
      const role = pegEl.dataset?.pegRole || (pegEl.id && pegEl.id.includes('front') ? 'front' : 'rear');
      const isFront = role === 'front';
      const youCpu = who === 'p1' ? 'you' : 'cpu';
      const name = who === 'p1' ? (state?.names?.p1 || 'Player') : (state?.names?.p2 || (settings?.vsHuman ? 'Opponent' : 'Computer'));
      const score = (state?.scores && (state.scores[who] !== undefined ? state.scores[who] : state.scores[youCpu])) || 0;
      const hole = (pegState && pegState[who] && pegState[who][role] !== undefined)
        ? pegState[who][role]
        : (role === 'front' ? score : ((state?.prev && (state.prev[who] !== undefined ? state.prev[who] : state.prev[youCpu])) || 0));
      const targetH = settings?.target || 121;
      const avId = BoardRenderer.getTrackAvatarId(who, settings);
      const av = AvatarManager.getAvatarById(avId);
      const avBadge = AvatarManager.renderAvatarBadge(av.id, { size: 22, showRing: true });

      return {
        icon: avBadge,
        title: `${isFront ? 'Leading Peg' : 'Trailing Peg'} · ${name}`,
        badge: `Hole ${hole}`,
        badgeClass: 'peg',
        body: `${name} (${av.name}) ${isFront ? 'front leading scoring peg' : 'rear follow peg'}. Currently resting at Hole ${hole}.${who === 'p1' ? ' (Click peg to change persona)' : ''}`,
        detail: `Current Score: ${score} pts`,
        extra: hole >= targetH ? '★ In Finish Cup' : `${Math.max(0, targetH - hole)} pts to win`
      };
    }

    // 2. Obstacles with custom dataset
    const obstacleEl = target.closest('[data-obstacle]');
    if (obstacleEl) {
      const obs = obstacleEl.dataset.obstacle;
      const hole = parseInt(obstacleEl.dataset.hole || '0', 10);
      const step = obstacleEl.dataset.step ? parseInt(obstacleEl.dataset.step, 10) : null;
      const street = typeof getStreetOrTurnName === 'function' ? getStreetOrTurnName(hole) : '';

      if (obs === 'detour-step') {
        return {
          icon: '⚠️',
          title: `Detour Bypass · Step ${step} of 7`,
          badge: 'DETOUR',
          badgeClass: 'bypass',
          body: `Intermediate peg hole ${step} along the 7-hole detour bypass. Pegs advance step-by-step through here before re-entering the main track at Hole ${hole + 1}.`,
          detail: `Rejoins Track at Hole ${hole + 1}`,
          extra: '+7 Extra Holes'
        };
      }
      if (obs === 'detour-box') {
        return {
          icon: '⚠️',
          title: `Detour Loop Bypass (Hole ${hole})`,
          badge: 'DETOUR',
          badgeClass: 'bypass',
          body: `A 7-hole bypass loop triggered by landing on Hole ${hole}. Bypasses the main track and rejoins at Hole ${hole + 1}.`,
          detail: `Rejoins Track at Hole ${hole + 1}`,
          extra: '7-Hole Bypass Loop'
        };
      }
      if (obs === 'roundabout-step') {
        return {
          icon: '🔄',
          title: `Round About Rotary · Hole ${step} of 7`,
          badge: 'ROTARY',
          badgeClass: 'rotary',
          body: `Rotary loop hole ${step} of 7. Pegs navigate around this scenic traffic circle before emerging back onto the track at Hole ${hole + 1}.`,
          detail: `Rejoins Track at Hole ${hole + 1}`,
          extra: '+7 Rotary Steps'
        };
      }
      if (obs === 'roundabout-box') {
        return {
          icon: '🔄',
          title: `Round About Rotary (Hole ${hole})`,
          badge: 'ROTARY',
          badgeClass: 'rotary',
          body: `A 7-hole traffic rotary roundabout triggered by landing on Hole ${hole}. Takes your peg on a 7-step loop before rejoining at Hole ${hole + 1}.`,
          detail: `Rejoins Track at Hole ${hole + 1}`,
          extra: '7 Rotary Holes'
        };
      }
      if (obs === 'roundabout-island') {
        return {
          icon: '🔄',
          title: `Rotary Island (Hole ${hole})`,
          badge: 'ROTARY',
          badgeClass: 'rotary',
          body: `Central rotary roundabout island. Pegs circle around this island between Hole ${hole} and Hole ${hole + 1}.`,
          detail: `Rotary at Hole ${hole}`,
          extra: street
        };
      }
      if (obs === 'jail-step') {
        return {
          icon: '⛓️',
          title: `Jail Escape · Step ${step} of 3`,
          badge: 'JAIL',
          badgeClass: 'trap',
          body: `Jail escape step ${step} of 3. Advance 1 step per missed turn to break out and return to track Hole ${hole}.`,
          detail: 'Miss 3 Turns to Escape',
          extra: street
        };
      }
      if (obs === 'jail-cell') {
        return {
          icon: '⛓️',
          title: `County Jail Cell (Hole ${hole})`,
          badge: 'JAIL TRAP',
          badgeClass: 'trap',
          body: `Landing on Hole ${hole} locks your peg behind iron bars! You must miss 3 consecutive turns before escaping back to the track.`,
          detail: 'Loss of 3 Turns',
          extra: street
        };
      }
      if (obs === 'bar-step') {
        const stepNum = Number(step);
        const drinkNames = { 1: 'Glass of Beer 🍺', 2: 'Glass of Wine 🍷', 3: 'Shot Glass 🥃' };
        const drinkName = drinkNames[stepNum] || `Drink ${step}`;
        const drinkIcons = { 1: '🍺', 2: '🍷', 3: '🥃' };
        return {
          icon: drinkIcons[stepNum] || '🍸',
          title: `The Bar Lounge · ${drinkName} (Step ${step} of 3)`,
          badge: 'THE BAR',
          badgeClass: 'trap',
          body: `Lounge refreshment: enjoying a ${drinkName.toLowerCase()} (step ${step} of 3) before returning to the track at Hole ${hole}.`,
          detail: 'Miss 3 Turns',
          extra: street
        };
      }
      if (obs === 'bar-box' || obs === 'bar-cell') {
        return {
          icon: '🍻',
          title: `The Bar Lounge (Hole ${hole})`,
          badge: 'LOUNGE TRAP',
          badgeClass: 'trap',
          body: `Landing on Hole ${hole} sends your peg into The Bar for refreshments! Miss 3 turns while enjoying beer, wine, and shots.`,
          detail: 'Loss of 3 Turns',
          extra: street
        };
      }
      if (obs === 'timeout-step') {
        return {
          icon: '🪑',
          title: `Timeout Corner · Penalty Stool (Step ${step} of 3)`,
          badge: 'TIMEOUT',
          badgeClass: 'trap',
          body: `Sitting on the penalty stool with the dunce cap: step ${step} of 3 before returning to the track at Hole ${hole}.`,
          detail: 'Miss 3 Turns',
          extra: street
        };
      }
      if (obs === 'timeout-box' || obs === 'timeout-cell') {
        return {
          icon: '🪑',
          title: `Timeout Corner (Hole ${hole})`,
          badge: 'TIMEOUT TRAP',
          badgeClass: 'trap',
          body: `Landing on Hole ${hole} sends your peg to the Timeout Corner! Sit on the penalty stool with the dunce cap for 3 turns.`,
          detail: 'Loss of 3 Turns',
          extra: street
        };
      }
      if (obs === 'jump-arrow') {
        const jId = obstacleEl.dataset.jumpId;
        const s = parseInt(obstacleEl.dataset.start, 10);
        const e = parseInt(obstacleEl.dataset.end, 10);
        const d = parseInt(obstacleEl.dataset.dest, 10);
        const isFwd = obstacleEl.dataset.forward === 'true' || d > s;
        const isSingle = s === e;
        const spanStr = isSingle ? `Hole ${s}` : `Holes ${s}–${e}`;
        return {
          icon: isFwd ? '🚀' : '↩️',
          title: `Jump ${jId}: ${spanStr} ➔ Hole ${d}`,
          badge: isFwd ? `Jump +${d - s}` : `Jump -${s - d}`,
          badgeClass: isFwd ? 'jump-fwd' : 'jump-bwd',
          body: isFwd
            ? `Rocket leap! Landing on Hole ${s} instantly teleports your peg forward to Hole ${d}!`
            : `Hazardous pitfall! Landing on Holes ${s}–${e} (3 peg holes) throws your peg backward to Hole ${d}.`,
          detail: `Trigger: ${spanStr} ➔ Dest: ${d}`,
          extra: isFwd ? '⚡ Forward Boost (1 hole)' : '⚠️ Backward Hazard (3 holes)'
        };
      }
      if (obs === 'sign-billboard') {
        const signId = parseInt(obstacleEl.dataset.signId || '1', 10);
        const signs = typeof getSignConfigs === 'function' ? getSignConfigs() : [];
        const signCfg = signs.find(s => s.id === signId);
        const remCount = (state && state.signsState && state.signsState[signId]) ? state.signsState[signId].length : (signCfg ? signCfg.initialSigns.length : 0);
        const totalCount = signCfg ? signCfg.initialSigns.length : 0;
        const s = obstacleEl.dataset.start || (signCfg ? signCfg.start : '');
        const e = obstacleEl.dataset.end || (signCfg ? signCfg.end : '');
        return {
          icon: '🪧',
          title: `Road Sign Block ${signId}`,
          badge: `${remCount}/${totalCount} Signs`,
          badgeClass: 'amber',
          body: `Interactive Road Signs zone spanning Holes ${s}–${e}. Passing or landing here triggers road signs with points and effects! Click billboard to view.`,
          detail: `Active Holes ${s}–${e}`,
          extra: '🪧 Click to Inspect'
        };
      }
      if (obs === 'stop-sign') {
        const hole = obstacleEl.dataset.hole || '';
        return {
          icon: '🛑',
          title: `Stop Sign (Hole ${hole})`,
          badge: 'STOP SIGN',
          badgeClass: 'trap',
          body: `1-hole obstacle! When reached during counting (pegging, hand, or crib), counting stops immediately at this hole.`,
          detail: 'Counting Stops Immediately',
          extra: '🛑 Full Stop'
        };
      }
      if (obs === 'no-passing') {
        const s = obstacleEl.dataset.start || '';
        const e = obstacleEl.dataset.end || '';
        const h = obstacleEl.dataset.hole || '';
        return {
          icon: '🚫',
          title: `No-Passing Single Lane (Holes ${s}–${e})`,
          badge: 'NO PASSING',
          badgeClass: 'trap',
          body: `Narrow single-file track spanning Holes ${s}–${e}. Pegs cannot jump or pass other pegs in this bottleneck lane!`,
          detail: `Bottleneck Zone: Holes ${s}–${e}`,
          extra: street || (h ? `Hole ${h}` : '🚫 Single File')
        };
      }
      if (obs === 'sign-block') {
        const blockId = obstacleEl.dataset.blockId || '';
        const s = obstacleEl.dataset.start || '';
        const e = obstacleEl.dataset.end || '';
        return {
          icon: '🪧',
          title: `Road Sign Block ${blockId} (Holes ${s}–${e})`,
          badge: 'SIGN ZONE',
          badgeClass: 'amber',
          body: `Interactive road signs zone covering Holes ${s}–${e}. Pegs landing here or passing through reveal road signs with bonuses and penalties!`,
          detail: `Active Billboard Block ${blockId}`,
          extra: street || '🪧 Road Signs'
        };
      }
      if (obs === 'skunk-marker') {
        const sDefs = BoardRenderer.getSkunkDefaults((settings && settings.target) || 481);
        const isDouble = hole === ((settings && settings.doubleSkunkMarkerHole) || sDefs.doubleSkunkHole);
        return {
          icon: '🦨',
          title: isDouble ? `Double Skunk Line · Hole ${hole}` : `Skunk Line · Hole ${hole}`,
          badge: isDouble ? 'DBL SKUNK' : 'SKUNK',
          badgeClass: isDouble ? 'trap' : 'amber',
          body: isDouble
            ? `Critical margin! Players failing to reach Hole ${hole} when the winner finishes suffer a double skunk defeat (counts as 3 losses).`
            : `Single skunk threshold! Crossing Hole ${hole} before the winner crosses the finish cup saves you from being skunked (counts as 2 losses).`,
          detail: `Threshold at Hole ${hole}`,
          extra: isDouble ? '🦨 3-Point Loss Penalty' : '🦨 2-Point Loss Penalty'
        };
      }
      if (obs === 'pints-space' || obs === 'pints-drink') {
        const type = obstacleEl.dataset.type || '';
        const hole = obstacleEl.dataset.hole || '';
        const drinkingNames = {
          youDrink: { name: 'You Drink', icon: '🍺', desc: 'Take a drink! Landing on this space requires the player to take a drink.' },
          guysDrink: { name: 'Guys Drink', icon: '🍻', desc: 'Guys Drink! All male players take a drink when someone lands here.' },
          galsDrink: { name: 'Gals Drink', icon: '🍷', desc: 'Gals Drink! All female players take a drink when someone lands here.' },
          leftDrink: { name: 'Left Drinks', icon: '👈', desc: 'Player to your left takes a drink when you land on this space.' },
          rightDrink: { name: 'Right Drinks', icon: '👉', desc: 'Player to your right takes a drink when you land on this space.' },
          everyoneDrink: { name: 'Everyone Drinks', icon: '🥂', desc: 'Social! Every player at the table takes a drink.' },
          everyoneButYouDrink: { name: 'Everyone But You', icon: '🎉', desc: 'Celebration! Everyone except the pegging player takes a drink.' },
          pickWhoDrinks: { name: 'Pick Who Drinks', icon: '🎯', desc: 'Target! Choose any player at the table to take a drink.' }
        };
        const item = drinkingNames[type] || { name: 'Drinking Space', icon: '🍺', desc: 'Take a drink!' };
        return {
          icon: item.icon,
          title: `${item.name} (Hole ${hole})`,
          badge: 'PINTS & PEGS',
          badgeClass: 'amber',
          body: item.desc,
          detail: `Drinking Space at Hole ${hole}`,
          extra: street || '🍻 Pints & Pegs'
        };
      }
      if (obs === 'adult-space' || obs === 'adult-dare') {
        const type = obstacleEl.dataset.type || '';
        const hole = obstacleEl.dataset.hole || '';
        const timed = obstacleEl.dataset.timed === 'true' || obstacleEl.dataset.timed === true;
        const adultDefs = {
          loseClothing: { name: 'You Lose Clothing', icon: '👙', desc: 'Player loses an article of clothing!' },
          gainClothing: { name: 'You Gain Clothing', icon: '🧥', desc: 'Player gains an article of clothing back!' },
          pickWhoLosesClothing: { name: 'Pick Who Strips', icon: '🎯', desc: 'Player picks who loses an article of clothing!' },
          rightLosesClothing: { name: 'Right Neighbor Strips', icon: '👉', desc: 'Person to right loses an article of clothing!' },
          leftLosesClothing: { name: 'Left Neighbor Strips', icon: '👈', desc: 'Person to left loses an article of clothing!' },
          removeClothingChoice: { name: 'Remove Clothing Choice', icon: '✂️', desc: 'Remove an article of clothing off person of your choice!' },
          touchBreast: { name: 'Touch Breast', icon: '💋', desc: 'Timed adult dare: Touch breast!' },
          lickBreast: { name: 'Lick Breast', icon: '👅', desc: 'Timed adult dare: Lick breast!' },
          suckBreast: { name: 'Suck Breast', icon: '👄', desc: 'Timed adult dare: Suck breast!' },
          touchGenitals: { name: 'Touch Genitals', icon: '✨', desc: 'Timed adult dare: Touch genitals!' },
          lickGenitals: { name: 'Lick Genitals', icon: '👅', desc: 'Timed adult dare: Lick genitals!' },
          suckGenitals: { name: 'Suck Genitals', icon: '👄', desc: 'Timed adult dare: Suck genitals!' },
          fingerGenitals: { name: 'Finger Genitals', icon: '👉', desc: 'Timed adult dare: Finger genitals!' },
          genitalIntercourse: { name: 'Genital Intercourse', icon: '🔥', desc: 'Timed adult dare: Genital intercourse!' },
          touchAnus: { name: 'Touch Anus', icon: '🍑', desc: 'Timed adult dare: Touch anus!' },
          lickAnus: { name: 'Lick Anus', icon: '👅', desc: 'Timed adult dare: Lick anus!' },
          fingerAnus: { name: 'Finger Anus', icon: '👉', desc: 'Timed adult dare: Finger anus!' },
          analIntercourse: { name: 'Anal Intercourse', icon: '🔥', desc: 'Timed adult dare: Anal intercourse!' }
        };
        const item = adultDefs[type] || { name: 'Adult Challenge', icon: '💋', desc: 'Adult challenge space!' };
        return {
          icon: item.icon,
          title: `${item.name} (Hole ${hole})`,
          badge: timed ? 'TIMED DARE' : 'STRIP SPACE',
          badgeClass: timed ? 'trap' : 'amber',
          body: item.desc,
          detail: `${timed ? 'Timed Dare' : 'Strip Space'} at Hole ${hole}`,
          extra: street || (timed ? '⏳ Timed Challenge' : '💋 Skunked & Stripped')
        };
      }
    }

    // 3. Track Holes
    const holeEl = target.closest('[data-hole]');
    if (holeEl) {
      const h = parseInt(holeEl.dataset.hole, 10);
      const targetHoles = (settings && settings.target) || 121;
      const isGameHole = h === targetHoles;
      const street = typeof getStreetOrTurnName === 'function' ? getStreetOrTurnName(h) : '';

      if (h === 0) {
        return {
          icon: '🏁',
          title: 'Starting Line · Hole 0',
          badge: 'START',
          badgeClass: '',
          body: 'The opening gate where pegs stage before cards are dealt and pegging begins.',
          detail: `${targetHoles} holes to win`,
          extra: 'Preshow'
        };
      }

      if (isGameHole) {
        return {
          icon: '🏆',
          title: `Game Hole ${h} · Finish Cup`,
          badge: 'VICTORY',
          badgeClass: 'milestone',
          body: 'The championship cup! Peg into or beyond this hole to win the cribbage match.',
          detail: `Target: ${targetHoles} Points`,
          extra: '🏁 Finish Line'
        };
      }

      const isStopSign = settings && settings.optStopSign && typeof getStopSignConfigs === 'function' && getStopSignConfigs().some(s => s.hole === h);
      if (isStopSign) {
        return {
          icon: '🛑',
          title: `Stop Sign (Hole ${h})`,
          badge: 'STOP SIGN',
          badgeClass: 'trap',
          body: '1-hole obstacle! When reached during counting, counting stops immediately at this hole.',
          detail: 'Counting Stops Immediately',
          extra: street
        };
      }

      const isJail = settings && settings.optJail && typeof getJailConfigs === 'function' && getJailConfigs().some(j => j.hole === h);
      if (isJail) {
        return {
          icon: '⛓️',
          title: `Jail Trap (Hole ${h})`,
          badge: 'JAIL TRAP',
          badgeClass: 'trap',
          body: 'Danger! Landing directly on this hole sends your peg behind iron bars for 3 turns.',
          detail: 'Miss 3 Turns',
          extra: street
        };
      }

      const isBar = settings && settings.optBar && typeof getBarConfigs === 'function' && getBarConfigs().some(b => b.hole === h);
      if (isBar) {
        return {
          icon: '🍸',
          title: `The Bar Trap (Hole ${h})`,
          badge: 'BAR TRAP',
          badgeClass: 'trap',
          body: 'Danger! Landing directly on this hole pulls your peg into the bar lounge for 3 turns.',
          detail: 'Miss 3 Turns',
          extra: street
        };
      }

      const isTimeout = settings && settings.optTimeout && typeof getTimeoutConfigs === 'function' && getTimeoutConfigs().some(t => t.hole === h);
      if (isTimeout) {
        return {
          icon: '⏳',
          title: `Timeout Corner (Hole ${h})`,
          badge: 'TIMEOUT TRAP',
          badgeClass: 'trap',
          body: 'Danger! Landing directly on this hole sends your peg to the timeout corner for 3 turns.',
          detail: 'Miss 3 Turns',
          extra: street
        };
      }

      // Jumps (Trigger & Landing Pad)
      if (settings && settings.optJump) {
        const jumps = (typeof getJumpConfigs === 'function' ? getJumpConfigs() : (ObstacleGeometry?.getJumpConfigs ? ObstacleGeometry.getJumpConfigs(settings) : []));
        if (Array.isArray(jumps)) {
          const jumpTrigger = jumps.find(j => (j.isForward ? (j.start === h) : (h >= j.start && h <= j.end)));
          if (jumpTrigger) {
            const isFwd = jumpTrigger.isForward;
            return {
              icon: isFwd ? '🚀' : '↩️',
              title: `Jump ${jumpTrigger.id} Trigger · Hole ${h}`,
              badge: isFwd ? `Jump +${jumpTrigger.dest - jumpTrigger.start}` : `Pitfall -${jumpTrigger.start - jumpTrigger.dest}`,
              badgeClass: isFwd ? 'jump-fwd' : 'jump-bwd',
              body: isFwd
                ? `Rocket leap trigger! Landing on Hole ${h} immediately teleports your peg forward to Hole ${jumpTrigger.dest}!`
                : `Hazardous pitfall trigger! Landing on Hole ${h} throws your peg backward to Hole ${jumpTrigger.dest}.`,
              detail: `Teleports to Hole ${jumpTrigger.dest}`,
              extra: street
            };
          }
          const jumpDest = jumps.find(j => j.dest === h);
          if (jumpDest) {
            return {
              icon: '🎯',
              title: `Jump ${jumpDest.id} Landing Pad · Hole ${h}`,
              badge: 'JUMP DEST',
              badgeClass: 'milestone',
              body: `Safe landing destination for Jump ${jumpDest.id} (originating from Hole ${jumpDest.start}).`,
              detail: `Destination from Hole ${jumpDest.start}`,
              extra: street
            };
          }
        }
      }

      // Detour Bypass Trigger
      if (settings && settings.optDetour) {
        const detours = (typeof getDetourConfigs === 'function' ? getDetourConfigs() : (ObstacleGeometry?.getDetourConfigs ? ObstacleGeometry.getDetourConfigs(settings) : []));
        const detour = Array.isArray(detours) ? detours.find(d => d.hole === h) : null;
        if (detour) {
          return {
            icon: '⚠️',
            title: `Detour Loop Trigger (Hole ${h})`,
            badge: 'DETOUR',
            badgeClass: 'bypass',
            body: `Detour branch point! Landing on Hole ${h} diverts your peg through a 7-hole bypass loop before re-entering at Hole ${h + 1}.`,
            detail: `Rejoins Track at Hole ${h + 1}`,
            extra: street
          };
        }
      }

      // Round About Rotary Trigger
      if (settings && settings.optRoundAbout) {
        const roundabouts = (typeof getRoundAboutConfigs === 'function' ? getRoundAboutConfigs() : (ObstacleGeometry?.getRoundAboutConfigs ? ObstacleGeometry.getRoundAboutConfigs(settings) : []));
        const rAbout = Array.isArray(roundabouts) ? roundabouts.find(r => r.hole === h) : null;
        if (rAbout) {
          return {
            icon: '🔄',
            title: `Round About Rotary Trigger (Hole ${h})`,
            badge: 'ROTARY',
            badgeClass: 'rotary',
            body: `Rotary circle trigger! Landing on Hole ${h} sends your peg circling a 7-hole traffic rotary before rejoining at Hole ${h + 1}.`,
            detail: `Rejoins Track at Hole ${h + 1}`,
            extra: street
          };
        }
      }

      // Dice Challenge Space
      if (settings && settings.optDice) {
        const diceConfigs = (typeof getDiceConfigs === 'function' ? getDiceConfigs() : (ObstacleGeometry?.getDiceConfigs ? ObstacleGeometry.getDiceConfigs(settings) : []));
        const dieHit = Array.isArray(diceConfigs) ? diceConfigs.find(d => d.hole === h) : null;
        if (dieHit) {
          return {
            icon: '🎲',
            title: `Dice Space (Hole ${h})`,
            badge: 'DICE',
            badgeClass: 'rotary',
            body: `3-Die Challenge! Landing on Hole ${h} immediately rolls 3 dice for Direction (±), Points (5..30), and Target Player!`,
            detail: `Roll The Dice at Hole ${h}`,
            extra: street
          };
        }
      }

      // Spinning Wheel Space
      if (settings && (settings.optWheel || settings.optSpinningWheel)) {
        const wheelConfigs = (typeof getWheelConfigs === 'function' ? getWheelConfigs() : (ObstacleGeometry?.getWheelConfigs ? ObstacleGeometry.getWheelConfigs(settings) : []));
        const wheelHit = Array.isArray(wheelConfigs) ? wheelConfigs.find(w => w.hole === h) : null;
        if (wheelHit) {
          return {
            icon: '🎡',
            title: `Spinning Wheel Space (Hole ${h})`,
            badge: 'WHEEL',
            badgeClass: 'rotary',
            body: `Spinning Wheel Prize Space! Landing on Hole ${h} spins the prize wheel for bonus points and game effects!`,
            detail: `Spin The Wheel at Hole ${h}`,
            extra: street
          };
        }
      }

      // Road Sign Billboard Zone
      if (settings && settings.optSign) {
        const signs = (typeof getSignConfigs === 'function' ? getSignConfigs() : (ObstacleGeometry?.getSignConfigs ? ObstacleGeometry.getSignConfigs(settings) : []));
        const signZone = Array.isArray(signs) ? signs.find(s => h >= s.start && h <= s.end) : null;
        if (signZone) {
          return {
            icon: '🪧',
            title: `Road Sign Block ${signZone.id} (Hole ${h})`,
            badge: 'SIGN ZONE',
            badgeClass: 'amber',
            body: `Interactive road signs zone covering Holes ${signZone.start}–${signZone.end}. Pegs landing here or passing through reveal road signs with bonuses and penalties!`,
            detail: `Active Billboard Block ${signZone.id}`,
            extra: street
          };
        }
      }

      // Skunk & Double Skunk Markers
      if (settings && settings.optSkunkMarkers) {
        const sDefs = BoardRenderer.getSkunkDefaults(targetHoles);
        const skHole = settings.skunkMarkerHole || sDefs.skunkHole;
        const dblSkHole = settings.doubleSkunkMarkerHole || sDefs.doubleSkunkHole;
        if (h === dblSkHole) {
          return {
            icon: '🦨',
            title: `Double Skunk Line · Hole ${h}`,
            badge: 'DBL SKUNK',
            badgeClass: 'trap',
            body: `Critical threshold! Opponents trailing behind this line when the winner finishes suffer a double skunk defeat (3 losses).`,
            detail: `Double Skunk at Hole ${h}`,
            extra: street
          };
        }
        if (h === skHole) {
          return {
            icon: '🦨',
            title: `Skunk Line · Hole ${h}`,
            badge: 'SKUNK LINE',
            badgeClass: 'amber',
            body: `Single skunk boundary! Players must cross Hole ${h} before the opponent finishes to avoid being skunked (2 losses).`,
            detail: `Skunk at Hole ${h}`,
            extra: street
          };
        }
      }

      // Stop Sign Mandatory Halt Zone
      if (settings && (settings.optStopSign || settings.optStopSigns || isClashTarget || brand === 'clash')) {
        const stopSigns = (typeof getStopSignConfigs === 'function' ? getStopSignConfigs() : (ObstacleGeometry?.getStopSignConfigs ? ObstacleGeometry.getStopSignConfigs(settings) : []));
        const stopSign = Array.isArray(stopSigns) ? stopSigns.find(s => s.hole === h) : null;
        if (stopSign) {
          return {
            icon: '🛑',
            title: `Stop Sign · Hole ${h}`,
            badge: 'STOP SIGN',
            badgeClass: 'trap',
            body: `Mandatory halt! Any peg passing or landing on Hole ${h} must stop immediately, burning any remaining count points.`,
            detail: `Mandatory Stop at Hole ${h}`,
            extra: street
          };
        }
      }

      // No-Passing Single Lane Zone
      if (settings && (settings.optNoPassingLanes || settings.optNoPassing || isClashTarget || brand === 'clash')) {
        const npLanes = (typeof getNoPassingLanes === 'function' ? getNoPassingLanes() : (ObstacleGeometry?.getNoPassingLanes ? ObstacleGeometry.getNoPassingLanes(settings) : []));
        const np = Array.isArray(npLanes) ? npLanes.find(l => h >= l.start && h <= l.end) : null;
        if (np) {
          return {
            icon: '🚫',
            title: `No-Passing Single Lane (Hole ${h})`,
            badge: 'NO PASSING',
            badgeClass: 'trap',
            body: `Narrow single-file track spanning Holes ${np.start}–${np.end}. Pegs cannot pass other pegs in this lane!`,
            detail: `Lanes ${np.start}–${np.end}`,
            extra: street
          };
        }
      }

      // Default hole representation
      const isMilestone = h > 0 && h % 5 === 0;
      return {
        icon: isMilestone ? '📍' : '⚪',
        title: `Track Hole ${h}`,
        badge: isMilestone ? `Hole ${h}` : '',
        badgeClass: isMilestone ? 'milestone' : '',
        body: `Standard board hole along the ${street || 'track'}. Pegs advance through here during gameplay.`,
        detail: `${Math.max(0, targetHoles - h)} holes to finish`,
        extra: street
      };
    }

    return null;
  }

  /**
   * Hides the active fancy tooltip immediately.
   */
  static hideFancyTooltip() {
    if (typeof document === 'undefined') return;
    const tip = document.getElementById('boardFancyTooltip');
    if (!tip) return;
    tip.classList.remove('is-visible');
    tip.setAttribute('aria-hidden', 'true');
    setTimeout(() => {
      if (!tip.classList.contains('is-visible')) {
        tip.style.display = 'none';
      }
    }, 120);
  }

  /**
   * Displays the fancy interactive tooltip card at given viewport coordinates.
   * @param {Object} data
   * @param {number} clientX
   * @param {number} clientY
   */
  static showFancyTooltip(data, clientX, clientY) {
    if (typeof document === 'undefined' || !data) return;
    const tip = document.getElementById('boardFancyTooltip');
    if (!tip) return;

    tip.innerHTML = `
      <div class="bft-header">
        <span class="bft-icon">${data.icon || '🔘'}</span>
        <div class="bft-title-wrap">
          <span class="bft-title">${data.title}</span>
        </div>
        ${data.badge ? `<span class="bft-badge ${data.badgeClass || ''}">${data.badge}</span>` : ''}
      </div>
      ${data.body ? `<div class="bft-body">${data.body}</div>` : ''}
      ${(data.detail || data.extra) ? `
        <div class="bft-footer">
          <span class="bft-detail">${data.detail || ''}</span>
          <span class="bft-extra">${data.extra || ''}</span>
        </div>
      ` : ''}
    `;

    tip.style.display = 'block';
    tip.setAttribute('aria-hidden', 'false');
    BoardRenderer.positionFancyTooltip(clientX, clientY);

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        tip.classList.add('is-visible');
      });
    } else {
      tip.classList.add('is-visible');
    }
  }

  /**
   * Positions the fancy tooltip card within viewport bounds.
   * @param {number} clientX
   * @param {number} clientY
   */
  static positionFancyTooltip(clientX, clientY) {
    if (typeof document === 'undefined') return;
    const tip = document.getElementById('boardFancyTooltip');
    if (!tip || tip.style.display === 'none') return;

    const pad = 14;
    const tipW = tip.offsetWidth || 230;
    const tipH = tip.offsetHeight || 80;

    let x = clientX + pad;
    let y = clientY + pad;

    const winW = (typeof window !== 'undefined') ? window.innerWidth : 1000;
    const winH = (typeof window !== 'undefined') ? window.innerHeight : 800;

    if (x + tipW > winW - 12) x = clientX - tipW - pad;
    if (x < 10) x = 10;
    if (y + tipH > winH - 12) y = clientY - tipH - pad;
    if (y < 10) y = 10;

    tip.style.left = `${x}px`;
    tip.style.top = `${y}px`;
  }

  /**
   * Initializes fancy interactive tooltips on the board.
   */
  static initBoardFancyTooltips(containerOrContext = null, maybeContext = {}) {
    if (typeof document === 'undefined') return;
    let container = null;
    let context = {};
    if (containerOrContext && (containerOrContext.nodeType || typeof containerOrContext.addEventListener === 'function')) {
      container = containerOrContext;
      context = maybeContext || {};
    } else if (containerOrContext && typeof containerOrContext === 'object') {
      context = containerOrContext;
      container = null;
    } else {
      context = maybeContext || {};
    }

    const cribBoard = container || document.getElementById('cribBoard');
    if (!cribBoard || cribBoard._fancyTooltipsInited) return;
    cribBoard._fancyTooltipsInited = true;

    let _boardTooltipTimer = null;
    let _boardTooltipRaf = null;
    let _currentTooltipTarget = null;

    const showFancyTooltip = (data, clientX, clientY) => {
      _currentTooltipTarget = _currentTooltipTarget || document.body;
      BoardRenderer.showFancyTooltip(data, clientX, clientY);
    };

    const positionFancyTooltip = (clientX, clientY) => {
      BoardRenderer.positionFancyTooltip(clientX, clientY);
    };

    const hideFancyTooltip = () => {
      const tip = document.getElementById('boardFancyTooltip');
      if (!tip) return;
      _currentTooltipTarget = null;
      if (_boardTooltipRaf) {
        cancelAnimationFrame(_boardTooltipRaf);
        _boardTooltipRaf = null;
      }
      tip.classList.remove('is-visible');
      tip.setAttribute('aria-hidden', 'true');
      if (_boardTooltipTimer) clearTimeout(_boardTooltipTimer);
      _boardTooltipTimer = setTimeout(() => {
        if (!tip.classList.contains('is-visible')) {
          tip.style.display = 'none';
        }
      }, 120);
    };

    const resolveContext = () => {
      const liveCtx = (typeof window !== 'undefined' && typeof window.getBoardTooltipContext === 'function')
        ? window.getBoardTooltipContext()
        : BoardRenderer.getBoardTooltipContext(context);
      return { ...context, ...liveCtx };
    };

    cribBoard.addEventListener('pointerover', (e) => {
      const data = BoardRenderer.getBoardTooltipData(e.target, resolveContext());
      if (data) {
        _currentTooltipTarget = e.target;
        showFancyTooltip(data, e.clientX, e.clientY);
      } else if (_currentTooltipTarget && !cribBoard.contains(e.target)) {
        hideFancyTooltip();
      }
    });

    cribBoard.addEventListener('pointermove', (e) => {
      const tip = document.getElementById('boardFancyTooltip');
      if (!tip || tip.style.display === 'none') {
        const data = BoardRenderer.getBoardTooltipData(e.target, resolveContext());
        if (data) {
          _currentTooltipTarget = e.target;
          showFancyTooltip(data, e.clientX, e.clientY);
        }
        return;
      }
      const matchingEl = e.target.closest('[data-hole], [data-obstacle], [data-peg-who], .peg-group');
      if (matchingEl && matchingEl !== _currentTooltipTarget) {
        const data = BoardRenderer.getBoardTooltipData(e.target, resolveContext());
        if (data) {
          _currentTooltipTarget = matchingEl;
          showFancyTooltip(data, e.clientX, e.clientY);
        }
      }
      positionFancyTooltip(e.clientX, e.clientY);
    });

    cribBoard.addEventListener('pointerout', (e) => {
      if (!e.relatedTarget || !cribBoard.contains(e.relatedTarget)) {
        hideFancyTooltip();
      }
    });

    cribBoard.addEventListener('pointerdown', hideFancyTooltip);
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', hideFancyTooltip, { passive: true });
    }
  }

  /**
   * Constructs the interactive collapsible accordion legend.
   */
  static createLegend(context = {}) {
    if (typeof document === 'undefined') return null;
    const { brand = 'crazy', settings = {}, adultOptions = [], obstacleConfigs = {} } = context;

    const container = document.createElement('div');
    container.className = 'clash-legend-container';
    container.id = 'clashLegendContainer';

    const isCollapsed = (typeof window !== 'undefined' && window._clashLegendCollapsed !== undefined)
      ? Boolean(window._clashLegendCollapsed)
      : true;
    const isPintsMode = brand === 'pints';
    const isAdultMode = brand === 'adult';

    let blocks = [];
    if (isAdultMode && Array.isArray(adultOptions) && adultOptions.length > 0) {
      blocks = adultOptions.map(opt => {
        const active = Boolean(settings && settings[opt.chkId]);
        const count = (settings && settings[opt.key + 'Count']) || 2;
        const pegs = (settings && settings[opt.key + 'Pegs']) || 1;
        const pegStr = pegs > 1 ? ` (${pegs} Pegs)` : '';
        const timedDesc = opt.timed ? ' Landing triggers an interactive timer (15–120s) and alarm!' : '';
        return {
          id: opt.key,
          title: `${opt.name}${pegStr}`,
          icon: opt.icon,
          badgeColor: opt.color,
          badgeBg: `${opt.color}2e`,
          active: active,
          count: count,
          desc: `${opt.actionText}.${timedDesc}`,
          selector: `#serpentineBoard [data-adult-key="${opt.key}"]`
        };
      });
    } else if (isPintsMode) {
      const pintsDefs = [
        { id: 'youDrink', name: 'You Drink', chkId: 'optYouDrink', icon: '🍺', color: '#f59e0b', desc: 'Take a drink! Landing on this space requires the player to take a drink.' },
        { id: 'guysDrink', name: 'Guys Drink', chkId: 'optGuysDrink', icon: '🍻', color: '#06b6d4', desc: 'Gentlemen drink! Landing on this space requires all guys playing to take a drink.' },
        { id: 'galsDrink', name: 'Gals Drink', chkId: 'optGalsDrink', icon: '🍷', color: '#ec4899', desc: 'Ladies drink! Landing on this space requires all gals playing to take a drink.' },
        { id: 'leftDrink', name: 'Left Drinks', chkId: 'optLeftDrink', icon: '👈', color: '#8b5cf6', desc: 'Player to the left drinks! Landing on this space requires the player to your left to drink.' },
        { id: 'rightDrink', name: 'Right Drinks', chkId: 'optRightDrink', icon: '👉', color: '#3b82f6', desc: 'Player to the right drinks! Landing on this space requires the player to your right to drink.' },
        { id: 'everyoneDrink', name: 'Everyone Drinks', chkId: 'optEveryoneDrink', icon: '🥂', color: '#10b981', desc: 'Everyone drinks! Landing on this space requires all players to take a drink.' },
        { id: 'everyoneButYouDrink', name: 'Everyone But You', chkId: 'optEveryoneButYouDrink', icon: '🎉', color: '#f97316', desc: 'Everyone else drinks! Landing on this space requires all players except you to drink.' },
        { id: 'pickWhoDrinks', name: 'Pick Who Drinks', chkId: 'optPickWhoDrinks', icon: '🎯', color: '#e11d48', desc: 'Pick who drinks! Landing on this space allows you to choose any player to take a drink.' }
      ];

      blocks = pintsDefs.map(dt => {
        const active = Boolean(settings && settings[dt.chkId]);
        const count = (settings && settings[dt.id + 'Count']) || 2;
        const pegs = (settings && settings[dt.id + 'Pegs']) || 1;
        const pegStr = pegs > 1 ? ` (${pegs} Pegs)` : '';
        return {
          id: dt.id,
          title: `${dt.name}${pegStr}`,
          icon: dt.icon,
          badgeColor: dt.color,
          badgeBg: `${dt.color}2e`,
          active: active,
          count: count,
          desc: dt.desc,
          selector: `#serpentineBoard .hole-${dt.id.replace(/([A-Z])/g, '-$1').toLowerCase()}`
        };
      });
    } else {
      const npLanes = obstacleConfigs.noPassingLanes || [];
      const jumps = obstacleConfigs.jumps || [];
      const jails = obstacleConfigs.jails || [];
      const bars = obstacleConfigs.bars || [];
      const timeouts = obstacleConfigs.timeouts || [];
      const detours = obstacleConfigs.detours || [];
      const roundabouts = obstacleConfigs.roundabouts || [];
      const signs = obstacleConfigs.signs || [];
      const stopSigns = obstacleConfigs.stopSigns || [];
      const dice = obstacleConfigs.dice || [];

      blocks = [
        {
          id: 'noPassing',
          title: 'No Passing Lane',
          icon: '🚫',
          badgeColor: '#00e5ff',
          badgeBg: 'rgba(0, 229, 255, 0.15)',
          active: Boolean(settings && settings.optNoPassingLanes),
          count: npLanes.length || 4,
          desc: 'Single-file bottleneck zone. Opponent pegs cannot pass while inside this lane — must wait behind.',
          selector: '#serpentineBoard .hole-no-passing-lane, #serpentineBoard .no-passing-lane-bg'
        },
        {
          id: 'jump',
          title: 'Jump',
          icon: '↗',
          badgeColor: '#22c55e',
          badgeBg: 'rgba(34, 197, 94, 0.18)',
          active: Boolean(settings && settings.optJump),
          count: jumps.length || 8,
          desc: 'Instant leap! Forward jumps are 1 hole; backward jumps span 3 holes. Green arrows propel forward; Red arrows drop backward.',
          selector: '#serpentineBoard #jumpsLayer'
        },
        {
          id: 'jail',
          title: 'Jail Cell',
          icon: '⛓️',
          badgeColor: '#f59e0b',
          badgeBg: 'rgba(245, 158, 11, 0.18)',
          active: Boolean(settings && settings.optJail),
          count: jails.length || 2,
          desc: 'Prison trap! Lock behind bars and miss 3 turns (1 step per missed turn) before escaping.',
          selector: '#serpentineBoard #jailsLayer'
        },
        {
          id: 'bar',
          title: 'The Bar',
          icon: '🍸',
          badgeColor: '#fb923c',
          badgeBg: 'rgba(251, 146, 60, 0.18)',
          active: Boolean(settings && settings.optBar),
          count: bars.length || 2,
          desc: 'Lounge trap! Stopped for drinks — miss 3 turns (1 step per missed turn) before returning.',
          selector: '#serpentineBoard #barsLayer'
        },
        {
          id: 'timeout',
          title: 'Timeout Corner',
          icon: '⏳',
          badgeColor: '#c084fc',
          badgeBg: 'rgba(192, 132, 252, 0.18)',
          active: Boolean(settings && settings.optTimeout),
          count: timeouts.length || 2,
          desc: 'Penalty corner! Sent to cool off — miss 3 turns (1 step per missed turn) before release.',
          selector: '#serpentineBoard #timeoutsLayer'
        },
        {
          id: 'detour',
          title: 'Detour Loop',
          icon: '🔄',
          badgeColor: '#fbbf24',
          badgeBg: 'rgba(251, 191, 36, 0.18)',
          active: Boolean(settings && settings.optDetour),
          count: detours.length || 2,
          desc: 'Forced diversion! Peg must travel an extra 7-hole road loop before rejoining the track.',
          selector: '#serpentineBoard #detoursLayer'
        },
        {
          id: 'roundabout',
          title: 'Roundabout',
          icon: '🔀',
          badgeColor: '#00e5ff',
          badgeBg: 'rgba(0, 229, 255, 0.18)',
          active: Boolean(settings && settings.optRoundAbout),
          count: roundabouts.length || 2,
          desc: 'Traffic circle! Navigate 7 rotary holes around the roundabout before continuing along track.',
          selector: '#serpentineBoard #roundAboutsLayer'
        },
        {
          id: 'signs',
          title: 'Road Signs',
          icon: '🪧',
          badgeColor: '#34d399',
          badgeBg: 'rgba(52, 211, 153, 0.18)',
          active: Boolean(settings && settings.optSigns),
          count: signs.length || 4,
          desc: 'Billboard zone! Landing here draws random action cards with point bonuses or penalties.',
          selector: '#serpentineBoard #signsLayer'
        },
        {
          id: 'stopsign',
          title: 'Stop Sign',
          icon: '🛑',
          badgeColor: '#ef4444',
          badgeBg: 'rgba(239, 68, 68, 0.18)',
          active: Boolean(settings && settings.optStopSign),
          count: stopSigns.length || 2,
          desc: '1-hole trap! When reached, counting stops immediately (pegging, hand, or crib).',
          selector: '#serpentineBoard #stopSignsLayer, #serpentineBoard .hole-stop-sign'
        },
        {
          id: 'dice',
          title: 'Dice',
          icon: '🎲',
          badgeColor: '#a855f7',
          badgeBg: 'rgba(168, 85, 247, 0.18)',
          active: Boolean(settings && settings.optDice),
          count: dice.length || 4,
          desc: '3-Die challenge space! Landing here rolls 3 dice for direction (±), point values (5..30), and target player.',
          selector: '#serpentineBoard #diceLayer, #serpentineBoard .hole-dice-trigger'
        },
        {
          id: 'wheel',
          title: 'Spinning Wheel',
          icon: '🎡',
          badgeColor: '#ec4899',
          badgeBg: 'rgba(236, 72, 153, 0.18)',
          active: Boolean(settings && (settings.optWheel || settings.optSpinningWheel)),
          count: (obstacleConfigs.wheels || []).length || 4,
          desc: 'Spinning Wheel challenge space! Landing here spins the prize wheel for bonus points and effects.',
          selector: '#serpentineBoard #wheelsLayer, #serpentineBoard .hole-wheel-trigger'
        },
        {
          id: 'skunkMarkers',
          title: 'Skunk Markers',
          icon: '🦨',
          badgeColor: '#94a3b8',
          badgeBg: 'rgba(148, 163, 184, 0.18)',
          active: Boolean(settings && settings.optSkunkMarkers),
          count: 2,
          desc: 'Milestone markers measuring victory margins. Skunk at 75% of route; Double Skunk at 50% of route.',
          selector: '#serpentineBoard #skunkMarkersLayer'
        }
      ];
    }

    const legendTitle = isAdultMode ? 'Skunked and Stripped Action Legend' : (isPintsMode ? 'Pints and Pegs Rules Legend' : 'Crazy Cribbage Obstacles Legend');
    const legendIcon = isAdultMode ? '🔥' : (isPintsMode ? '🍺' : '🎲');

    container.innerHTML = `
      <div class="clash-legend-header">
        <div class="clash-legend-title">
          <span class="clash-legend-icon">${legendIcon}</span>
          <span>${legendTitle}</span>
          <span class="clash-legend-hint">(hover to highlight)</span>
        </div>
        <button type="button" class="clash-legend-toggle-btn" id="btnToggleClashLegend" aria-expanded="${!isCollapsed}" title="Toggle Legend View">
          <span id="clashLegendToggleIcon">${isCollapsed ? '＋' : '−'}</span>
          <span id="clashLegendToggleText">${isCollapsed ? 'Show' : 'Hide'}</span>
        </button>
      </div>
      <div class="clash-legend-grid ${isCollapsed ? 'collapsed' : ''}" id="clashLegendGrid">
        ${blocks.map(b => `
          <div class="clash-legend-card ${b.active ? 'active-block' : 'inactive-block'}" data-block-id="${b.id}" data-selector="${b.selector}" title="${b.title} (${b.active ? 'Active on board' : 'Disabled'})">
            <div class="clash-legend-card-header">
              <span class="clash-legend-badge" style="background:${b.badgeBg}; color:${b.badgeColor}; border: 1px solid ${b.badgeColor}40;">
                ${b.icon} ${b.title}
              </span>
              <span class="clash-legend-status ${b.active ? 'status-active' : 'status-off'}">
                ${b.active ? `${b.count} On Board` : 'Off'}
              </span>
            </div>
            <div class="clash-legend-card-body">
              ${b.desc}
            </div>
          </div>
        `).join('')}
      </div>
    `;

    const toggleBtn = container.querySelector('#btnToggleClashLegend');
    const grid = container.querySelector('#clashLegendGrid');
    const toggleIcon = container.querySelector('#clashLegendToggleIcon');
    const toggleText = container.querySelector('#clashLegendToggleText');

    if (toggleBtn && grid) {
      toggleBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const nowCollapsed = !grid.classList.contains('collapsed');
        if (typeof window !== 'undefined') window._clashLegendCollapsed = nowCollapsed;
        grid.classList.toggle('collapsed', nowCollapsed);
        toggleBtn.setAttribute('aria-expanded', String(!nowCollapsed));
        if (toggleIcon) toggleIcon.textContent = nowCollapsed ? '＋' : '−';
        if (toggleText) toggleText.textContent = nowCollapsed ? 'Show' : 'Hide';
      });
    }

    const cards = container.querySelectorAll('.clash-legend-card');
    cards.forEach(card => {
      const sel = card.getAttribute('data-selector');
      if (!sel) return;

      const setHighlight = (enable) => {
        try {
          const els = document.querySelectorAll(sel);
          els.forEach(el => {
            if (enable) el.classList.add('legend-highlight-pulse');
            else el.classList.remove('legend-highlight-pulse');
          });
        } catch (err) {}
      };

      card.addEventListener('mouseenter', () => setHighlight(true));
      card.addEventListener('mouseleave', () => setHighlight(false));
      card.addEventListener('click', () => {
        setHighlight(true);
        setTimeout(() => setHighlight(false), 2000);
      });
    });

    return container;
  }

  /**
   * Helper to map seat ID to peg gradient ID, respecting custom peg skins for Player 1.
   * @param {string} who
   * @param {Object} [settings={}]
   * @returns {string}
   */
  static getPegGradId(who, settings = {}) {
    const isP1 = (who === 'p1' || who === 'you' || who === 'team1');
    if (isP1 && settings && settings.pegSkin && settings.pegSkin !== 'brass' && settings.pegSkin !== 'classic' && settings.pegSkin !== 'avatar') {
      return `pegSkin_${settings.pegSkin}`;
    }
    if (isP1) return 'pegP1Grad';
    if (who === 'p2' || who === 'cpu' || who === 'team2') return 'pegP2Grad';
    if (who === 'p3') return 'pegP3Grad';
    if (who === 'p4') return 'pegP4Grad';
    if (who === 'p5') return 'pegP5Grad';
    if (who === 'p6') return 'pegP6Grad';
    if (who === 'p7' || who === 'team3') return 'pegP7Grad';
    if (who === 'p8' || who === 'team4') return 'pegP8Grad';
    return 'pegP1Grad';
  }

  /**
   * Helper to resolve the assigned avatar persona ID for a given track/seat.
   * @param {string} who
   * @param {Object} [settings={}]
   * @returns {string}
   */
  static getTrackAvatarId(who, settings = {}) {
    const isP1 = (who === 'p1' || who === 'you' || who === 'team1');
    if (isP1) {
      const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
      return avMgr ? avMgr.getSelectedAvatarId() : 'joker';
    }
    // Check if configured in seats roster
    if (settings && Array.isArray(settings.seats)) {
      const s = settings.seats.find(x => x && (x.id === who || (who === 'cpu' && x.id === 'p2')));
      if (s && s.avatar) {
        if (s.avatar === 'custom' && !isP1) {
          return 'custom_' + s.id;
        }
        return s.avatar;
      }
    }
    // Computer AI profile mapping
    if (who === 'p2' || who === 'cpu' || who === 'team2') {
      const isVsHuman = Boolean(settings && (settings.vsHuman || settings.isHvH || settings.opp === 'human'));
      if (isVsHuman) return 'queen';
      const rawProf = (settings && settings.optAiProfile) || 'shark';
      const avatarMap = {
        master: 'king',
        maverick: 'steampunk',
        regular: 'velvet_fox',
        casual: 'pint_master',
        rookie: 'jack',
        gambler: 'gambler',
        turtler: 'turtler',
        shark: 'shark'
      };
      if (avatarMap[rawProf]) return avatarMap[rawProf];
      const aiProfile = (typeof window !== 'undefined' && window.CribbageAI?.normalizeProfile)
        ? window.CribbageAI.normalizeProfile(rawProf)
        : rawProf;
      return avatarMap[aiProfile] || 'shark';
    }
    // Multiplayer seats
    const multiplayerAvatars = {
      p3: 'pint_master',
      p4: 'steampunk',
      p5: 'clash_helm',
      p6: 'velvet_fox',
      p7: 'cannon_commander',
      p8: 'lightning_29',
      team3: 'pint_master',
      team4: 'steampunk'
    };
    return multiplayerAvatars[who] || 'jack';
  }

  /**
   * Resolves track accent color for rims and halo badges.
   * @param {string} who
   * @returns {string}
   */
  static getTrackColor(who) {
    const colors = {
      p1: '#d4a017',
      you: '#d4a017',
      team1: '#d4a017',
      p2: '#ef4444',
      cpu: '#ef4444',
      team2: '#ef4444',
      p3: '#10b981',
      p4: '#3b82f6',
      p5: '#a855f7',
      p6: '#ec4899',
      p7: '#f97316',
      team3: '#10b981',
      p8: '#06b6d4',
      team4: '#3b82f6'
    };
    return colors[who] || '#d4a017';
  }

  /**
   * Generates SVG inner markup for a 2D cribbage peg.
   * Implements Phase 2: custom avatar medallion fill, high-contrast track rim,
   * glossy 3D dome reflection for front pegs, and dimmed indicator for rear follow pegs.
   * @param {string} who
   * @param {string} role ('front' | 'rear')
   * @param {Object} [settings={}]
   * @returns {string}
   */
  static buildPegSvgContent(who, role, settings = {}) {
    const isFront = (role === 'front');
    const isP1 = (who === 'p1' || who === 'you' || who === 'team1');
    const trackColor = BoardRenderer.getTrackColor(who);

    // If P1 explicitly selected a non-avatar metallic peg skin (silver, cherry, walnut, glow), support classic metallic look
    const skin = settings && settings.pegSkin;
    const hasCustomMetallicSkin = isP1 && skin && skin !== 'brass' && skin !== 'classic' && skin !== 'avatar';

    if (hasCustomMetallicSkin) {
      const gradId = `pegSkin_${skin}`;
      return `
        <circle cx="0" cy="0" r="${isFront ? '5.2' : '4.4'}"
                fill="url(#${gradId})"
                stroke="#1a1208" stroke-width="0.8"
                opacity="${isFront ? '1' : '0.85'}"
                data-peg-who="${who}" data-peg-role="${role}" />
        <circle cx="-1.2" cy="-1.4" r="1.4" fill="#ffffff" opacity="0.6"
                data-peg-who="${who}" data-peg-role="${role}" />
      `;
    }

    const patId = `pegAvatarPattern_${who}`;
    const outerR = isFront ? '5.8' : '4.6';
    const innerR = isFront ? '5.2' : '4.0';
    const strokeW = isFront ? '1.2' : '1.0';
    const opacity = isFront ? '1.0' : '0.85';

    return `
      <!-- Bezel halo / track color rim -->
      <circle cx="0" cy="0" r="${outerR}"
              fill="#1a1208" stroke="${trackColor}" stroke-width="${strokeW}"
              opacity="${opacity}"
              data-peg-who="${who}" data-peg-role="${role}" />
      <!-- Avatar medallion fill -->
      <circle cx="0" cy="0" r="${innerR}"
              fill="url(#${patId})"
              opacity="${opacity}"
              data-peg-who="${who}" data-peg-role="${role}" />
      ${!isFront ? `
        <!-- Trailing follow peg subtle dim overlay -->
        <circle cx="0" cy="0" r="${innerR}" fill="#000000" opacity="0.18" pointer-events="none" />
        <circle cx="-1.0" cy="-1.0" r="1.1" fill="#ffffff" opacity="0.4" pointer-events="none" />
      ` : `
        <!-- Leading scoring peg 3D glossy reflection -->
        <ellipse cx="-1.4" cy="-1.6" rx="2.1" ry="1.2" fill="#ffffff" opacity="0.45"
                 transform="rotate(-25 -1.4 -1.6)" pointer-events="none" />
      `}
    `;
  }

  /**
   * Returns gradient colors and border styling for the selected woodwork finish.
   * @param {string} [finishKey='maple']
   * @returns {Object}
   */
  static getBoardFinishConfig(finishKey = 'maple') {
    const finishes = {
      maple: {
        stops: [
          { offset: '0%', color: '#eed5ad' },
          { offset: '45%', color: '#e2c191' },
          { offset: '80%', color: '#d4af7a' },
          { offset: '100%', color: '#c69e67' }
        ],
        stroke: '#6b3a12',
        innerStroke: 'rgba(255, 240, 215, 0.45)'
      },
      walnut: {
        stops: [
          { offset: '0%', color: '#5a3d28' },
          { offset: '45%', color: '#442b1a' },
          { offset: '80%', color: '#2f1a0e' },
          { offset: '100%', color: '#1f1008' }
        ],
        stroke: '#150904',
        innerStroke: 'rgba(225, 185, 150, 0.3)'
      },
      cherry: {
        stops: [
          { offset: '0%', color: '#8c3523' },
          { offset: '45%', color: '#6e2315' },
          { offset: '80%', color: '#4a1309' },
          { offset: '100%', color: '#2d0804' }
        ],
        stroke: '#240603',
        innerStroke: 'rgba(255, 200, 190, 0.35)'
      },
      obsidian: {
        stops: [
          { offset: '0%', color: '#2a2632' },
          { offset: '45%', color: '#181520' },
          { offset: '80%', color: '#100e16' },
          { offset: '100%', color: '#09070c' }
        ],
        stroke: '#d4af37',
        innerStroke: 'rgba(234, 179, 8, 0.4)'
      },
      driftwood: {
        stops: [
          { offset: '0%', color: '#cbd5e1' },
          { offset: '45%', color: '#94a3b8' },
          { offset: '80%', color: '#64748b' },
          { offset: '100%', color: '#475569' }
        ],
        stroke: '#334155',
        innerStroke: 'rgba(241, 245, 249, 0.5)'
      }
    };
    return finishes[finishKey] || finishes.maple;
  }

  /**
   * Helper to get skunk default hole numbers.
   */
  static getSkunkDefaults(target = 121) {
    const t = Number(target) || 121;
    if (t === 481) return { skunkHole: 360, doubleSkunkHole: 240 };
    if (t === 361) return { skunkHole: 270, doubleSkunkHole: 180 };
    if (t === 241) return { skunkHole: 180, doubleSkunkHole: 120 };
    return { skunkHole: 90, doubleSkunkHole: 60 };
  }

  /**
   * Constructs the full SVG cribbage board with obstacle overlays, milestone markers,
   * player pegs, and interactive legend.
   * @param {Object} options
   * @returns {Element|null}
   */
  static buildBoard(options = {}) {
    if (typeof document === 'undefined') return null;
    const {
      root = document.getElementById('boardTracks'),
      settings = {},
      state = {},
      pegState = {},
      brand = 'crazy',
      activeTracks = ['p1', 'p2'],
      getTrackXYFn = null,
      getTrackXY: optGetTrackXY = null,
      obstacleConfigs = {},
      obstacleStepHelpers = {},
      pintsConfigs = [],
      adultConfigs = [],
      createLegendFn = null,
      onAfterRender = null
    } = options;

    if (!root) return null;

    const signature = BoardRenderer.getBoardSignature(settings, activeTracks, brand);
    if (typeof window !== 'undefined') {
      window._currentBoardSignature = signature;
    }
    root.innerHTML = '';

    const isClashBrand = (brand === 'clash');
    const isClashTarget = isClashBrand && (!settings?.target || [241, 361, 481].includes(Number(settings.target)));
    const clashTarget = [241, 361, 481].includes(Number(settings?.target)) ? Number(settings.target) : 481;
    const curCfg = isClashTarget ? getClashBoardCfg(clashTarget) : BOARD_CFG;
    const boardViewW = curCfg.viewW;
    const boardViewH = curCfg.viewH;

    const cribBoard = (root && root.closest ? root.closest('.board') : null) || (typeof document !== 'undefined' ? document.getElementById('cribBoard') : null);
    const splitContainer = (typeof document !== 'undefined') ? document.querySelector('.table.split') : null;

    // If 'none' is chosen for boardViewMode, do not display any board in the game
    if (settings && settings.boardViewMode === 'none') {
      if (cribBoard) {
        cribBoard.classList.remove('is-3d-board', 'is-3d-room-mode');
        cribBoard.classList.add('is-no-board');
      }
      if (splitContainer) {
        splitContainer.classList.remove('is-3d-room-mode');
      }
      if (root) {
        root.innerHTML = '';
        root.style.display = 'none';
      }
      if (typeof window !== 'undefined' && window._currentRenderer3D) {
        window._currentRenderer3D.destroy();
        window._currentRenderer3D = null;
      }
      const staleResizeHandle = typeof document !== 'undefined' ? document.getElementById('board3DResizeHandle') : null;
      if (staleResizeHandle) {
        try {
          if (typeof staleResizeHandle.remove === 'function') {
            staleResizeHandle.remove();
          } else if (staleResizeHandle.parentNode) {
            staleResizeHandle.parentNode.removeChild(staleResizeHandle);
          }
        } catch (_) {}
      }
      if (typeof onAfterRender === 'function') {
        onAfterRender();
      }
      return null;
    }

    if (cribBoard) {
      cribBoard.classList.remove('is-no-board');
    }
    if (root) {
      const isMin = cribBoard && cribBoard.classList.contains('is-minimized');
      root.style.display = isMin ? 'none' : '';
    }

    const is3DMode = Boolean(settings && (settings.boardViewMode === '3d' || settings.boardViewMode === 'room3d' || settings.boardViewMode === 'room3d-fullscreen'));
    const isRoom3D = Boolean(settings && (settings.boardViewMode === 'room3d' || settings.boardViewMode === 'room3d-fullscreen'));

    if (splitContainer) {
      splitContainer.classList.toggle('is-3d-room-mode', isRoom3D);
    }

    if (is3DMode) {
      if (cribBoard) {
        cribBoard.classList.add('is-3d-board');
        cribBoard.classList.toggle('is-3d-room-mode', isRoom3D);
      }
      const allowedBoards = Board3DCatalog.getBoards(brand);
      const isAllowed = allowedBoards.some(b => b.id === settings.selected3DBoardId);
      const boardConfig = (isAllowed && Board3DCatalog.getBoardById(settings.selected3DBoardId)) || allowedBoards[0] || Board3DCatalog.getBoardById(settings.selected3DBoardId);
      if (boardConfig && boardConfig.id && settings) {
        settings.selected3DBoardId = boardConfig.id;
      }
      if (typeof window !== 'undefined' && window._currentRenderer3D) {
        window._currentRenderer3D.destroy();
        window._currentRenderer3D = null;
      }
      const oldResizeHandle = typeof document !== 'undefined' ? document.getElementById('board3DResizeHandle') : null;
      if (oldResizeHandle) {
        try {
          if (typeof oldResizeHandle.remove === 'function') {
            oldResizeHandle.remove();
          } else if (oldResizeHandle.parentNode) {
            oldResizeHandle.parentNode.removeChild(oldResizeHandle);
          }
        } catch (_) {}
      }

      let savedHeight = null;
      try {
        if (typeof localStorage !== 'undefined') {
          savedHeight = parseInt(localStorage.getItem('cc_board_3d_height'), 10);
        }
      } catch (_) {}

      const hasValidSavedHeight = Boolean(savedHeight && !isNaN(savedHeight) && savedHeight >= 140 && savedHeight <= 1600);

      const wrap = document.createElement('div');
      wrap.className = 'board-container board-3d-container is-resizable';
      wrap.id = 'board3DContainer';
      const defaultRoom3DHeight = 'height: clamp(540px, 75vh, 920px); min-height: 520px; aspect-ratio: auto;';
      const heightStyle = isRoom3D ? (hasValidSavedHeight ? `height: ${savedHeight}px; min-height: 480px; aspect-ratio: auto;` : defaultRoom3DHeight) : (hasValidSavedHeight ? `height: ${savedHeight}px; aspect-ratio: auto;` : `aspect-ratio: ${boardViewW} / ${boardViewH};`);
      wrap.style.cssText = `width: 100%; ${heightStyle} min-height: 140px; max-height: 85vh; position: relative; overflow: hidden; border-radius: 12px;`;
      root.appendChild(wrap);

      let roomLoader = null;
      if (is3DMode && !options.skipLoader) {
        try {
          const loaderCls = (typeof Room3DLoadingOverlay !== 'undefined') ? Room3DLoadingOverlay : (typeof window !== 'undefined' ? window.Room3DLoadingOverlay : null);
          if (loaderCls && typeof loaderCls.show === 'function') {
            const activeLoader = loaderCls.getActive();
            roomLoader = activeLoader || loaderCls.show(wrap, {
              settings,
              is3DOnly: !isRoom3D,
              cardBack: settings?.cardBack,
              theme: settings?.roomTheme || settings?.tableEnvironment,
              deck: (typeof window !== 'undefined' ? window.FULL_DECK : null)
            });
          }
        } catch (err) {
          console.warn('[BoardRenderer] Could not mount Room3DLoadingOverlay:', err);
        }
      }

      const resizeHandle = document.createElement('div');
      resizeHandle.className = 'board-3d-resize-handle';
      resizeHandle.id = 'board3DResizeHandle';
      resizeHandle.setAttribute('role', 'separator');
      resizeHandle.setAttribute('aria-orientation', 'horizontal');
      resizeHandle.setAttribute('tabindex', '0');
      resizeHandle.setAttribute('title', 'Drag to resize 3D board vertically (Double-click to reset height)');
      resizeHandle.innerHTML = `
        <div class="board-3d-resize-grip" aria-hidden="true"></div>
        <div class="board-3d-resize-label"><span>⇅ 3D Board Height</span></div>
        <div class="board-3d-resize-grip" aria-hidden="true"></div>
      `;
      root.appendChild(resizeHandle);

      let r3d = null;

      let isDragging = false;
      let startY = 0;
      let startH = 0;

      const updateHeight = (newH) => {
        const maxH = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.85) : 800;
        const clampedH = Math.round(Math.max(140, Math.min(maxH, newH)));
        wrap.style.height = `${clampedH}px`;
        wrap.style.aspectRatio = 'auto';
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('cc_board_3d_height', String(clampedH));
          }
        } catch (_) {}
        if (r3d && r3d.engine) {
          r3d.engine.resize();
        }
        return clampedH;
      };

      const onPointerMove = (e) => {
        if (!isDragging) return;
        if (e.cancelable) e.preventDefault();
        const clientY = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY;
        const deltaY = clientY - startY;
        updateHeight(startH + deltaY);
      };

      const onPointerUp = () => {
        if (!isDragging) return;
        isDragging = false;
        resizeHandle.classList.remove('dragging');
        if (typeof document !== 'undefined') {
          document.body.classList.remove('is-board-3d-resizing');
        }
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('touchmove', onPointerMove);
        window.removeEventListener('touchend', onPointerUp);
        if (r3d && r3d.handleResize) {
          r3d.handleResize();
        }
      };

      const onPointerDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        isDragging = true;
        startY = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY;
        startH = wrap.getBoundingClientRect().height;
        resizeHandle.classList.add('dragging');
        if (typeof document !== 'undefined') {
          document.body.classList.add('is-board-3d-resizing');
        }
        window.addEventListener('pointermove', onPointerMove, { passive: false });
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('touchend', onPointerUp);
      };

      const resetHeight = () => {
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('cc_board_3d_height');
          }
        } catch (_) {}
        wrap.style.height = '';
        wrap.style.aspectRatio = `${boardViewW} / ${boardViewH}`;
        if (r3d) {
          if (r3d.fitBoardToViewBox) {
            r3d.fitBoardToViewBox(true);
          }
          if (r3d.handleResize) {
            r3d.handleResize();
          }
        }
      };

      resizeHandle.addEventListener('pointerdown', onPointerDown);
      resizeHandle.addEventListener('dblclick', resetHeight);
      resizeHandle.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const curH = wrap.getBoundingClientRect().height;
          updateHeight(curH - 25);
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          const curH = wrap.getBoundingClientRect().height;
          updateHeight(curH + 25);
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          resetHeight();
        }
      });

      wrap.addEventListener('mouseup', () => {
        const curH = wrap.getBoundingClientRect().height;
        if (curH && curH >= 140) {
          try {
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem('cc_board_3d_height', String(Math.round(curH)));
            }
          } catch (_) {}
        }
      });

      r3d = new BoardRenderer3D(wrap, {
        boardConfig,
        activeTracks,
        settings,
        boardViewW,
        boardViewH,
        brand,
        obstacleConfigs,
        getTrackXYFn,
        onProgress: (statusText, percent) => {
          const loaderCls = (typeof Room3DLoadingOverlay !== 'undefined') ? Room3DLoadingOverlay : (typeof window !== 'undefined' ? window.Room3DLoadingOverlay : null);
          if (loaderCls && typeof loaderCls.updateProgress === 'function') {
            loaderCls.updateProgress(statusText, percent);
          } else if (roomLoader && typeof roomLoader.updateProgress === 'function') {
            roomLoader.updateProgress(statusText, percent);
          }
        },
        onReady: (inst) => {
          const loaderCls = (typeof Room3DLoadingOverlay !== 'undefined') ? Room3DLoadingOverlay : (typeof window !== 'undefined' ? window.Room3DLoadingOverlay : null);
          if (loaderCls && typeof loaderCls.markRendererReady === 'function') {
            loaderCls.markRendererReady(inst);
          } else if (roomLoader && typeof roomLoader.markRendererReady === 'function') {
            roomLoader.markRendererReady(inst);
          }
          if (typeof window !== 'undefined') window._currentRenderer3D = inst;
          inst.updatePegs(state?.scores || {}, state?.prev || {}, false);
          if (typeof inst.syncFromGameState === 'function') {
            inst.syncFromGameState({ state, settings });
          }
          if (typeof window !== 'undefined' && window.crazyCribbageApp && typeof window.crazyCribbageApp.updateBoardViewToggleBtn === 'function') {
            window.crazyCribbageApp.updateBoardViewToggleBtn();
          }
          if (typeof window !== 'undefined' && window.crazyCribbageApp && typeof window.crazyCribbageApp.syncInGameBoardSelector === 'function') {
            window.crazyCribbageApp.syncInGameBoardSelector();
          }
          if (typeof onAfterRender === 'function') onAfterRender();
          if (settings && settings.boardViewMode === 'room3d-fullscreen') {
            try {
              const gameEl = (typeof document !== 'undefined') ? document.getElementById('game') : null;
              const menuVariants = (typeof document !== 'undefined') ? document.getElementById('menuVariants') : null;
              const isGameActive = Boolean(gameEl && !gameEl.classList.contains('hidden'));
              const isVariantsOpen = Boolean(menuVariants && !menuVariants.classList.contains('hidden'));
              if (isGameActive && !isVariantsOpen && typeof inst.enterFullScreen === 'function') {
                inst.enterFullScreen();
              }
            } catch (fsErr) {
              console.warn('[BoardRenderer] Error entering fullscreen onReady:', fsErr);
            }
          }
        },
        onError: (err) => {
          const loaderCls = (typeof Room3DLoadingOverlay !== 'undefined') ? Room3DLoadingOverlay : (typeof window !== 'undefined' ? window.Room3DLoadingOverlay : null);
          if (loaderCls && typeof loaderCls.hideImmediate === 'function') {
            loaderCls.hideImmediate();
          } else if (roomLoader && typeof roomLoader.close === 'function') {
            roomLoader.close(false);
          }
          console.error('[BoardRenderer3D] Babylon failed to initialize, falling back to 2D:', err);
          if (cribBoard) cribBoard.classList.remove('is-3d-board');
          settings.boardViewMode = '2d';
          const el = typeof document !== 'undefined' ? document.getElementById('optBoardView') : null;
          if (el) el.value = '2d';
          const elModal = typeof document !== 'undefined' ? document.getElementById('optBoardViewModal') : null;
          if (elModal) elModal.value = '2d';
          if (typeof window !== 'undefined' && window.crazyCribbageApp && typeof window.crazyCribbageApp.updateBoardViewToggleBtn === 'function') {
            window.crazyCribbageApp.updateBoardViewToggleBtn();
          }
          BoardRenderer.buildBoard(options);
        }
      });
      return wrap;
    }

    if (cribBoard) cribBoard.classList.remove('is-3d-board', 'is-3d-room-mode');
    if (splitContainer) splitContainer.classList.remove('is-3d-room-mode');

    if (typeof window !== 'undefined' && window._currentRenderer3D) {
      window._currentRenderer3D.destroy();
      window._currentRenderer3D = null;
    }
    const staleResizeHandle = typeof document !== 'undefined' ? document.getElementById('board3DResizeHandle') : null;
    if (staleResizeHandle) {
      try {
        if (typeof staleResizeHandle.remove === 'function') {
          staleResizeHandle.remove();
        } else if (staleResizeHandle.parentNode) {
          staleResizeHandle.parentNode.removeChild(staleResizeHandle);
        }
      } catch (_) {}
    }

    const wrap = document.createElement('div');
    wrap.className = 'board-container';

    const bgX = 15;
    const bgY = isClashTarget ? 12 : 14;
    const bgW = boardViewW - 30;
    const bgH = boardViewH - (isClashTarget ? 24 : 28);
    const bgRx = isClashTarget ? 16 : 14;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'serpentineBoard';
    svg.setAttribute('viewBox', `0 0 ${boardViewW} ${boardViewH}`);

    const finishCfg = BoardRenderer.getBoardFinishConfig(settings?.boardFinish || 'maple');
    const boardWoodStops = finishCfg.stops.map(s => `<stop offset="${s.offset}" stop-color="${s.color}" />`).join('\n          ');

    const allPegTracks = Array.from(new Set([...(Array.isArray(activeTracks) ? activeTracks : []), 'p1', 'you', 'p2', 'cpu']));
    const pegPatternsDefs = allPegTracks.map(w => {
      const avId = BoardRenderer.getTrackAvatarId(w, settings);
      return AvatarManager.getAvatarPatternSvg(avId, `pegAvatarPattern_${w}`);
    }).join('\n        ');

    svg.innerHTML = `
      <defs>
        <radialGradient id="holeGrad" cx="35%" cy="35%" r="70%">
          <stop offset="0%" stop-color="#020101" />
          <stop offset="60%" stop-color="#120803" />
          <stop offset="100%" stop-color="#241005" />
        </radialGradient>
        <linearGradient id="boardWoodGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          ${boardWoodStops}
        </linearGradient>
        <linearGradient id="pegP1Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ffffff" />
          <stop offset="35%" stop-color="#f3d7a0" />
          <stop offset="85%" stop-color="#d4a017" />
          <stop offset="100%" stop-color="#7a5500" />
        </linearGradient>
        <linearGradient id="pegP2Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#fff0ea" />
          <stop offset="35%" stop-color="#f87171" />
          <stop offset="85%" stop-color="#dc2626" />
          <stop offset="100%" stop-color="#7f1d1d" />
        </linearGradient>
        <linearGradient id="pegP3Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ecfdf5" />
          <stop offset="35%" stop-color="#34d399" />
          <stop offset="85%" stop-color="#059669" />
          <stop offset="100%" stop-color="#064e3b" />
        </linearGradient>
        <linearGradient id="pegP4Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#eff6ff" />
          <stop offset="35%" stop-color="#60a5fa" />
          <stop offset="85%" stop-color="#2563eb" />
          <stop offset="100%" stop-color="#1e3a8a" />
        </linearGradient>
        <linearGradient id="pegP5Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f5d0fe" />
          <stop offset="35%" stop-color="#c084fc" />
          <stop offset="85%" stop-color="#a855f7" />
          <stop offset="100%" stop-color="#581c87" />
        </linearGradient>
        <linearGradient id="pegP6Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ffedd5" />
          <stop offset="35%" stop-color="#fb923c" />
          <stop offset="85%" stop-color="#ea580c" />
          <stop offset="100%" stop-color="#7c2d12" />
        </linearGradient>
        <linearGradient id="pegP7Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#cffafe" />
          <stop offset="35%" stop-color="#22d3ee" />
          <stop offset="85%" stop-color="#0891b2" />
          <stop offset="100%" stop-color="#164e63" />
        </linearGradient>
        <linearGradient id="pegP8Grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#fce7f3" />
          <stop offset="35%" stop-color="#f472b6" />
          <stop offset="85%" stop-color="#db2777" />
          <stop offset="100%" stop-color="#831843" />
        </linearGradient>
        <!-- Custom Peg Skins -->
        <linearGradient id="pegSkin_brass" href="#pegP1Grad" />
        <linearGradient id="pegSkin_silver" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ffffff" />
          <stop offset="35%" stop-color="#e2e8f0" />
          <stop offset="85%" stop-color="#94a3b8" />
          <stop offset="100%" stop-color="#334155" />
        </linearGradient>
        <linearGradient id="pegSkin_cherry" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#fed7aa" />
          <stop offset="35%" stop-color="#f87171" />
          <stop offset="85%" stop-color="#b91c1c" />
          <stop offset="100%" stop-color="#450a0a" />
        </linearGradient>
        <linearGradient id="pegSkin_walnut" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#e7dfd5" />
          <stop offset="35%" stop-color="#a88b73" />
          <stop offset="85%" stop-color="#5c4028" />
          <stop offset="100%" stop-color="#27180d" />
        </linearGradient>
        <linearGradient id="pegSkin_glow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ecfeff" />
          <stop offset="35%" stop-color="#38bdf8" />
          <stop offset="85%" stop-color="#0284c7" />
          <stop offset="100%" stop-color="#0369a1" />
        </linearGradient>
        <linearGradient id="pegYouGrad" href="#pegP1Grad" />
        <linearGradient id="pegCpuGrad" href="#pegP2Grad" />
        <linearGradient id="pegTeam1Grad" href="#pegP1Grad" />
        <linearGradient id="pegTeam2Grad" href="#pegP2Grad" />
        <linearGradient id="pegTeam3Grad" href="#pegP7Grad" />
        <linearGradient id="pegTeam4Grad" href="#pegP8Grad" />
        <filter id="pegShadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="1.8" flood-color="#000" flood-opacity="0.65" />
        </filter>
        <marker id="jumpArrowGreen" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#22c55e" />
        </marker>
        <marker id="jumpArrowRed" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#ef4444" />
        </marker>
        <!-- Phase 2: Avatar Medallion Peg Patterns -->
        ${pegPatternsDefs}
      </defs>
      <rect x="${bgX}" y="${bgY}" width="${bgW}" height="${bgH}" rx="${bgRx}"
            fill="url(#boardWoodGrad)" stroke="${finishCfg.stroke}" stroke-width="3" />
      <rect x="${bgX + 3}" y="${bgY + 3}" width="${bgW - 6}" height="${bgH - 6}" rx="${bgRx - 2}"
            fill="none" stroke="${finishCfg.innerStroke}" stroke-width="1.2" />
      <g id="jumpsLayer"></g>
      <g id="jailsLayer"></g>
      <g id="barsLayer"></g>
      <g id="timeoutsLayer"></g>
      <g id="detoursLayer"></g>
      <g id="roundAboutsLayer"></g>
      <g id="signsLayer"></g>
      <g id="stopSignsLayer"></g>
      <g id="diceLayer"></g>
      <g id="wheelsLayer"></g>
      <g id="trackHoles"></g>
      <g id="skunkMarkersLayer"></g>
      <g id="pegsLayer"></g>
    `;

    root.appendChild(wrap);
    wrap.appendChild(svg);

    const jumpsGroup = svg.querySelector('#jumpsLayer');
    const jailsGroup = svg.querySelector('#jailsLayer');
    const barsGroup = svg.querySelector('#barsLayer');
    const timeoutsGroup = svg.querySelector('#timeoutsLayer');
    const detoursGroup = svg.querySelector('#detoursLayer');
    const roundAboutsGroup = svg.querySelector('#roundAboutsLayer');
    const signsGroup = svg.querySelector('#signsLayer');
    const stopSignsGroup = svg.querySelector('#stopSignsLayer');
    const diceGroup = svg.querySelector('#diceLayer');
    const wheelsGroup = svg.querySelector('#wheelsLayer');
    const skunkMarkersGroup = svg.querySelector('#skunkMarkersLayer');
    const holesGroup = svg.querySelector('#trackHoles');
    const targetHoles = isClashTarget ? clashTarget : (settings.target === 61 ? 61 : (settings.target === 31 ? 31 : 121));

    const actualTrackXYFn = getTrackXYFn || optGetTrackXY;
    const getTrackXY = (h, who) => (typeof actualTrackXYFn === 'function' ? actualTrackXYFn(h, who, targetHoles) : defaultGetTrackXY(h, who, targetHoles, settings));
    const refTrack = activeTracks[0] || 'p1';
    const isClashObstaclesActive = (brand === 'clash');

    // 1. Jumps
    const jumps = isClashObstaclesActive ? (obstacleConfigs.jumps || ObstacleGeometry.getJumpConfigs(settings)) : [];
    if (isClashObstaclesActive && settings.optJump && jumpsGroup && Array.isArray(jumps)) {
      jumps.forEach(j => {
        if (j.start > targetHoles && j.dest > targetHoles) return;
        const sHole = Math.min(targetHoles, j.isForward ? j.start : j.start + 1);
        const dHole = Math.min(targetHoles, j.dest);
        const pStart = getTrackXY(sHole, refTrack);
        const pEnd = getTrackXY(dHole, refTrack);

        const dx = pEnd.x - pStart.x;
        const dy = pEnd.y - pStart.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 4) return;

        const mx = (pStart.x + pEnd.x) / 2;
        const my = (pStart.y + pEnd.y) / 2;
        const nx = -dy / dist;
        const ny = dx / dist;

        let arcSign = -1;
        if (Math.abs(dy) < 15) {
          arcSign = my > (isClashTarget ? (curCfg.viewH / 2) : BOARD_CFG.st2Y) ? 1 : -1;
        } else {
          arcSign = j.isForward ? -1 : 1;
        }
        const curveHeight = Math.max(16, Math.min(45, dist * 0.25)) * arcSign;
        const cx = mx + nx * curveHeight;
        const cy = my + ny * curveHeight;

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', `M ${pStart.x} ${pStart.y} Q ${cx} ${cy} ${pEnd.x} ${pEnd.y}`);
        path.classList.add(j.isForward ? 'jump-arrow-forward' : 'jump-arrow-backward');
        path.setAttribute('marker-end', j.isForward ? 'url(#jumpArrowGreen)' : 'url(#jumpArrowRed)');
        path.setAttribute('pointer-events', 'stroke');
        path.dataset.obstacle = 'jump-arrow';
        path.dataset.jumpId = String(j.id);
        path.dataset.start = String(j.start);
        path.dataset.end = String(j.end);
        path.dataset.dest = String(j.dest);
        path.dataset.forward = String(j.isForward);

        jumpsGroup.appendChild(path);
      });
    }

    // 2. Jails
    const jails = isClashObstaclesActive ? (obstacleConfigs.jails || ObstacleGeometry.getJailConfigs(settings)) : [];
    const getJailStepXY = obstacleStepHelpers.getJailStepXY || ((h, s, w) => ObstacleGeometry.getJailStepXY(h, s, w, settings, getTrackXY));
    if (isClashObstaclesActive && settings.optJail && jailsGroup && Array.isArray(jails)) {
      jails.forEach(j => {
        if (j.hole > targetHoles) return;
        const pTrack = getTrackXY(j.hole, refTrack);
        const cellPos = getJailStepXY(j.hole, 0, refTrack);

        const pathLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        pathLine.setAttribute('x1', cellPos.x);
        pathLine.setAttribute('y1', cellPos.y);
        pathLine.setAttribute('x2', pTrack.x);
        pathLine.setAttribute('y2', pTrack.y);
        pathLine.classList.add('jail-path-line');
        pathLine.dataset.obstacle = 'jail-cell';
        pathLine.dataset.hole = String(j.hole);
        jailsGroup.appendChild(pathLine);

        for (let s = 1; s <= 3; s++) {
          const stepPos = getJailStepXY(j.hole, s, refTrack);
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', stepPos.x);
          circle.setAttribute('cy', stepPos.y);
          circle.setAttribute('r', '2');
          circle.classList.add('hole-jail-path');
          circle.dataset.obstacle = 'jail-step';
          circle.dataset.hole = String(j.hole);
          circle.dataset.step = String(s);
          jailsGroup.appendChild(circle);
        }

        const boxW = 22;
        const boxH = 22;
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', cellPos.x - boxW / 2);
        rect.setAttribute('y', cellPos.y - boxH / 2);
        rect.setAttribute('width', boxW);
        rect.setAttribute('height', boxH);
        rect.setAttribute('rx', '4');
        rect.classList.add('jail-cell-box');
        rect.dataset.obstacle = 'jail-cell';
        rect.dataset.hole = String(j.hole);
        jailsGroup.appendChild(rect);

        [-5, 0, 5].forEach(dx => {
          const bar = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          bar.setAttribute('x1', cellPos.x + dx);
          bar.setAttribute('y1', cellPos.y - boxH / 2 + 2.5);
          bar.setAttribute('x2', cellPos.x + dx);
          bar.setAttribute('y2', cellPos.y + boxH / 2 - 2.5);
          bar.classList.add('jail-cell-bar');
          bar.style.pointerEvents = 'none';
          jailsGroup.appendChild(bar);
        });

        const cellHole = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        cellHole.setAttribute('cx', cellPos.x);
        cellHole.setAttribute('cy', cellPos.y);
        cellHole.setAttribute('r', '3.6');
        cellHole.classList.add('hole-jail-cell');
        cellHole.dataset.obstacle = 'jail-cell';
        cellHole.dataset.hole = String(j.hole);
        jailsGroup.appendChild(cellHole);
      });
    }

    // 3. Bars
    const bars = isClashObstaclesActive ? (obstacleConfigs.bars || ObstacleGeometry.getBarConfigs(settings)) : [];
    const getBarStepXY = obstacleStepHelpers.getBarStepXY || ((h, s, w) => ObstacleGeometry.getBarStepXY(h, s, w, settings, getTrackXY));
    if (isClashObstaclesActive && settings.optBar && barsGroup && Array.isArray(bars)) {
      bars.forEach(b => {
        if (b.hole > targetHoles) return;
        const pTrack = getTrackXY(b.hole, refTrack);
        const barPos = getBarStepXY(b.hole, 0, refTrack);

        const pathLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        pathLine.setAttribute('x1', barPos.x);
        pathLine.setAttribute('y1', barPos.y);
        pathLine.setAttribute('x2', pTrack.x);
        pathLine.setAttribute('y2', pTrack.y);
        pathLine.classList.add('bar-path-line');
        pathLine.dataset.obstacle = 'bar-box';
        pathLine.dataset.hole = String(b.hole);
        barsGroup.appendChild(pathLine);

        for (let s = 1; s <= 3; s++) {
          const stepPos = getBarStepXY(b.hole, s, refTrack);
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', stepPos.x);
          circle.setAttribute('cy', stepPos.y);
          circle.setAttribute('r', '2');
          circle.classList.add('hole-bar-path');
          circle.dataset.obstacle = 'bar-step';
          circle.dataset.hole = String(b.hole);
          circle.dataset.step = String(s);
          barsGroup.appendChild(circle);
        }

        const boxW = 24;
        const boxH = 22;
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', barPos.x - boxW / 2);
        rect.setAttribute('y', barPos.y - boxH / 2);
        rect.setAttribute('width', boxW);
        rect.setAttribute('height', boxH);
        rect.setAttribute('rx', '5');
        rect.classList.add('bar-lounge-box');
        rect.dataset.obstacle = 'bar-box';
        rect.dataset.hole = String(b.hole);
        barsGroup.appendChild(rect);

        const counter = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        counter.setAttribute('x1', barPos.x - boxW / 2 + 3);
        counter.setAttribute('y1', barPos.y - boxH / 2 + 4);
        counter.setAttribute('x2', barPos.x + boxW / 2 - 3);
        counter.setAttribute('y2', barPos.y - boxH / 2 + 4);
        counter.classList.add('bar-counter-line');
        counter.style.pointerEvents = 'none';
        barsGroup.appendChild(counter);

        const drinkIcon = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const ix = barPos.x + 5;
        const iy = barPos.y - 1;
        drinkIcon.setAttribute('d', `M ${ix - 3.5} ${iy - 4} L ${ix + 3.5} ${iy - 4} L ${ix} ${iy} Z M ${ix} ${iy} L ${ix} ${iy + 4} M ${ix - 2.5} ${iy + 4} L ${ix + 2.5} ${iy + 4}`);
        drinkIcon.classList.add('bar-icon');
        drinkIcon.style.pointerEvents = 'none';
        barsGroup.appendChild(drinkIcon);

        const barHole = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        barHole.setAttribute('cx', barPos.x - 4);
        barHole.setAttribute('cy', barPos.y + 1);
        barHole.setAttribute('r', '3.4');
        barHole.classList.add('hole-bar-lounge');
        barHole.dataset.obstacle = 'bar-box';
        barHole.dataset.hole = String(b.hole);
        barsGroup.appendChild(barHole);
      });
    }

    // 4. Timeouts
    const timeouts = isClashObstaclesActive ? (obstacleConfigs.timeouts || ObstacleGeometry.getTimeoutConfigs(settings)) : [];
    const getTimeoutStepXY = obstacleStepHelpers.getTimeoutStepXY || ((h, s, w) => ObstacleGeometry.getTimeoutStepXY(h, s, w, settings, getTrackXY));
    if (isClashObstaclesActive && settings.optTimeout && timeoutsGroup && Array.isArray(timeouts)) {
      timeouts.forEach(t => {
        if (t.hole > targetHoles) return;
        const pTrack = getTrackXY(t.hole, refTrack);
        const timeoutPos = getTimeoutStepXY(t.hole, 0, refTrack);

        const pathLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        pathLine.setAttribute('x1', timeoutPos.x);
        pathLine.setAttribute('y1', timeoutPos.y);
        pathLine.setAttribute('x2', pTrack.x);
        pathLine.setAttribute('y2', pTrack.y);
        pathLine.classList.add('timeout-path-line');
        pathLine.dataset.obstacle = 'timeout-box';
        pathLine.dataset.hole = String(t.hole);
        timeoutsGroup.appendChild(pathLine);

        for (let s = 1; s <= 3; s++) {
          const stepPos = getTimeoutStepXY(t.hole, s, refTrack);
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', stepPos.x);
          circle.setAttribute('cy', stepPos.y);
          circle.setAttribute('r', '2');
          circle.classList.add('hole-timeout-path');
          circle.dataset.obstacle = 'timeout-step';
          circle.dataset.hole = String(t.hole);
          circle.dataset.step = String(s);
          timeoutsGroup.appendChild(circle);
        }

        const boxW = 24;
        const boxH = 22;
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', timeoutPos.x - boxW / 2);
        rect.setAttribute('y', timeoutPos.y - boxH / 2);
        rect.setAttribute('width', boxW);
        rect.setAttribute('height', boxH);
        rect.setAttribute('rx', '5');
        rect.classList.add('timeout-corner-box');
        rect.dataset.obstacle = 'timeout-box';
        rect.dataset.hole = String(t.hole);
        timeoutsGroup.appendChild(rect);

        const seat = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        seat.setAttribute('x1', timeoutPos.x - boxW / 2 + 3);
        seat.setAttribute('y1', timeoutPos.y - boxH / 2 + 4);
        seat.setAttribute('x2', timeoutPos.x + boxW / 2 - 3);
        seat.setAttribute('y2', timeoutPos.y - boxH / 2 + 4);
        seat.classList.add('timeout-seat-line');
        seat.style.pointerEvents = 'none';
        timeoutsGroup.appendChild(seat);

        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const ix = timeoutPos.x + 5;
        const iy = timeoutPos.y - 1;
        icon.setAttribute('d', `M ${ix - 3} ${iy - 4} L ${ix + 3} ${iy - 4} L ${ix} ${iy} L ${ix - 3} ${iy + 4} L ${ix + 3} ${iy + 4} M ${ix - 3} ${iy + 4} L ${ix} ${iy} L ${ix + 3} ${iy - 4}`);
        icon.classList.add('timeout-icon');
        icon.style.pointerEvents = 'none';
        timeoutsGroup.appendChild(icon);

        const cornerHole = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        cornerHole.setAttribute('cx', timeoutPos.x - 4);
        cornerHole.setAttribute('cy', timeoutPos.y + 1);
        cornerHole.setAttribute('r', '3.4');
        cornerHole.classList.add('hole-timeout-corner');
        cornerHole.dataset.obstacle = 'timeout-box';
        cornerHole.dataset.hole = String(t.hole);
        timeoutsGroup.appendChild(cornerHole);
      });
    }

    // 5. Detours
    const detours = isClashObstaclesActive ? (obstacleConfigs.detours || ObstacleGeometry.getDetourConfigs(settings)) : [];
    const computeLoopGeometry = obstacleStepHelpers.computeLoopGeometry || ((h, w, r) => ObstacleGeometry.computeLoopGeometry(h, w, r, settings, getTrackXY));
    const getDetourStepXY = obstacleStepHelpers.getDetourStepXY || ((h, s, w) => ObstacleGeometry.getDetourStepXY(h, s, w, settings, getTrackXY));
    const catmullRomPath = obstacleStepHelpers.catmullRomPath || (pts => ObstacleGeometry.catmullRomPath(pts));
    if (isClashObstaclesActive && settings.optDetour && detoursGroup && Array.isArray(detours)) {
      detours.forEach(d => {
        if (d.hole > targetHoles) return;
        const geom = computeLoopGeometry(d.hole, refTrack, false);
        const points = [geom.pEntry];
        for (let s = 1; s <= 7; s++) {
          points.push(getDetourStepXY(d.hole, s, refTrack));
        }
        points.push(geom.pExit);

        const pathD = catmullRomPath(points);

        const roadBg = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        roadBg.setAttribute('d', pathD);
        roadBg.classList.add('detour-road-bg');
        roadBg.dataset.obstacle = 'detour-box';
        roadBg.dataset.hole = String(d.hole);
        detoursGroup.appendChild(roadBg);

        const roadStripe = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        roadStripe.setAttribute('d', pathD);
        roadStripe.classList.add('detour-road-path');
        roadStripe.style.pointerEvents = 'none';
        detoursGroup.appendChild(roadStripe);

        const apexPos = points[4];
        const badgeOffX = geom.Nx !== 0 ? (geom.Nx > 0 ? 8 : -30) : -11;
        const badgeOffY = geom.Ny !== 0 ? (geom.Ny > 0 ? 8 : -16) : -5;
        const detourBox = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        detourBox.setAttribute('x', apexPos.x + badgeOffX);
        detourBox.setAttribute('y', apexPos.y + badgeOffY);
        detourBox.setAttribute('width', 22);
        detourBox.setAttribute('height', 9);
        detourBox.setAttribute('rx', '3');
        detourBox.classList.add('detour-loop-box');
        detourBox.dataset.obstacle = 'detour-box';
        detourBox.dataset.hole = String(d.hole);
        detoursGroup.appendChild(detourBox);

        const detourText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        detourText.setAttribute('x', apexPos.x + badgeOffX + 11);
        detourText.setAttribute('y', apexPos.y + badgeOffY + 6.8);
        detourText.setAttribute('text-anchor', 'middle');
        detourText.setAttribute('fill', '#f59e0b');
        detourText.setAttribute('font-size', '5px');
        detourText.setAttribute('font-weight', '800');
        detourText.setAttribute('letter-spacing', '0.5px');
        detourText.textContent = 'DETOUR';
        detourText.style.pointerEvents = 'none';
        detoursGroup.appendChild(detourText);

        for (let s = 1; s <= 7; s++) {
          const pt = points[s];
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', pt.x);
          circle.setAttribute('cy', pt.y);
          circle.setAttribute('r', '3.0');
          circle.classList.add('hole-detour-loop');
          circle.dataset.obstacle = 'detour-step';
          circle.dataset.hole = String(d.hole);
          circle.dataset.step = String(s);
          detoursGroup.appendChild(circle);
        }
      });
    }

    // 6. Roundabouts
    const roundabouts = isClashObstaclesActive ? (obstacleConfigs.roundabouts || ObstacleGeometry.getRoundAboutConfigs(settings)) : [];
    const getRoundAboutStepXY = obstacleStepHelpers.getRoundAboutStepXY || ((h, s, w) => ObstacleGeometry.getRoundAboutStepXY(h, s, w, settings, getTrackXY));
    if (isClashObstaclesActive && settings.optRoundAbout && roundAboutsGroup && Array.isArray(roundabouts)) {
      roundabouts.forEach(r => {
        if (r.hole > targetHoles) return;
        const geom = computeLoopGeometry(r.hole, refTrack, true);
        const points = [geom.pEntry];
        for (let s = 1; s <= 7; s++) {
          points.push(getRoundAboutStepXY(r.hole, s, refTrack));
        }
        points.push(geom.pExit);

        const pathD = catmullRomPath(points);

        const roadBg = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        roadBg.setAttribute('d', pathD);
        roadBg.classList.add('roundabout-road-bg');
        roadBg.dataset.obstacle = 'roundabout-box';
        roadBg.dataset.hole = String(r.hole);
        roundAboutsGroup.appendChild(roadBg);

        const roadStripe = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        roadStripe.setAttribute('d', pathD);
        roadStripe.classList.add('roundabout-road-path');
        roadStripe.style.pointerEvents = 'none';
        roundAboutsGroup.appendChild(roadStripe);

        const island = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        island.setAttribute('cx', geom.Cx);
        island.setAttribute('cy', geom.Cy);
        island.setAttribute('r', '7.5');
        island.classList.add('roundabout-island');
        island.dataset.obstacle = 'roundabout-island';
        island.dataset.hole = String(r.hole);
        roundAboutsGroup.appendChild(island);

        const islandInner = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        islandInner.setAttribute('cx', geom.Cx);
        islandInner.setAttribute('cy', geom.Cy);
        islandInner.setAttribute('r', '5.5');
        islandInner.classList.add('roundabout-island-inner');
        islandInner.style.pointerEvents = 'none';
        roundAboutsGroup.appendChild(islandInner);

        const islandIcon = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        islandIcon.setAttribute('x', geom.Cx);
        islandIcon.setAttribute('y', geom.Cy + 0.5);
        islandIcon.setAttribute('text-anchor', 'middle');
        islandIcon.setAttribute('dominant-baseline', 'central');
        islandIcon.setAttribute('font-size', '7px');
        islandIcon.textContent = '🔄';
        islandIcon.style.pointerEvents = 'none';
        roundAboutsGroup.appendChild(islandIcon);

        const apexPos = points[4];
        const badgeOffX = geom.Nx !== 0 ? (geom.Nx > 0 ? 8 : -30) : -11;
        const badgeOffY = geom.Ny !== 0 ? (geom.Ny > 0 ? 8 : -16) : -5;
        const rBox = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rBox.setAttribute('x', apexPos.x + badgeOffX);
        rBox.setAttribute('y', apexPos.y + badgeOffY);
        rBox.setAttribute('width', 22);
        rBox.setAttribute('height', 9);
        rBox.setAttribute('rx', '3');
        rBox.classList.add('roundabout-loop-box');
        rBox.dataset.obstacle = 'roundabout-box';
        rBox.dataset.hole = String(r.hole);
        roundAboutsGroup.appendChild(rBox);

        const rText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        rText.setAttribute('x', apexPos.x + badgeOffX + 11);
        rText.setAttribute('y', apexPos.y + badgeOffY + 6.8);
        rText.setAttribute('text-anchor', 'middle');
        rText.setAttribute('fill', '#00e5ff');
        rText.setAttribute('font-size', '5px');
        rText.setAttribute('font-weight', '800');
        rText.setAttribute('letter-spacing', '0.5px');
        rText.textContent = 'ROTARY';
        rText.style.pointerEvents = 'none';
        roundAboutsGroup.appendChild(rText);

        for (let s = 1; s <= 7; s++) {
          const pt = points[s];
          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          circle.setAttribute('cx', pt.x);
          circle.setAttribute('cy', pt.y);
          circle.setAttribute('r', '3.0');
          circle.classList.add('hole-roundabout-loop');
          circle.dataset.obstacle = 'roundabout-step';
          circle.dataset.hole = String(r.hole);
          circle.dataset.step = String(s);
          roundAboutsGroup.appendChild(circle);
        }
      });
    }

    // 7. Signs
    const signs = isClashObstaclesActive ? (obstacleConfigs.signs || ObstacleGeometry.getSignConfigs(settings)) : [];
    if (isClashObstaclesActive && settings.optSigns && signsGroup && Array.isArray(signs)) {
      signs.forEach(b => {
        if (b.start > targetHoles) return;
        const effectiveEnd = Math.min(targetHoles, b.end);
        const pStart = getTrackXY(b.start, refTrack);

        let bracketD = `M ${pStart.x} ${pStart.y}`;
        for (let h = b.start + 1; h <= effectiveEnd; h++) {
          const pt = getTrackXY(h, refTrack);
          bracketD += ` L ${pt.x} ${pt.y}`;
        }

        const bracket = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        bracket.setAttribute('d', bracketD);
        bracket.classList.add('signs-bracket');
        signsGroup.appendChild(bracket);

        const bracketLine = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        bracketLine.setAttribute('d', bracketD);
        bracketLine.classList.add('signs-bracket-line');
        signsGroup.appendChild(bracketLine);

        const midHole = Math.round((b.start + effectiveEnd) / 2);
        const midPos = getTrackXY(midHole, refTrack);
        const offsetSign = midPos.y > (isClashTarget ? (curCfg.viewH / 2) : BOARD_CFG.st2Y) ? 1 : -1;
        const bbY = midPos.y + (offsetSign * 16);
        const bbW = 56;
        const bbH = 14;

        const bbRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bbRect.setAttribute('x', midPos.x - bbW / 2);
        bbRect.setAttribute('y', bbY - bbH / 2);
        bbRect.setAttribute('width', bbW);
        bbRect.setAttribute('height', bbH);
        bbRect.setAttribute('rx', '3');
        bbRect.classList.add('signs-billboard');
        bbRect.setAttribute('id', `signsBillboard${b.id}`);
        bbRect.setAttribute('onclick', `if (window.onSignBillboardClick) window.onSignBillboardClick(${b.id})`);
        bbRect.dataset.obstacle = 'sign-billboard';
        bbRect.dataset.signId = String(b.id);
        bbRect.dataset.start = String(b.start);
        bbRect.dataset.end = String(effectiveEnd);
        signsGroup.appendChild(bbRect);

        const remCount = (state && state.signsState && state.signsState[b.id]) ? state.signsState[b.id].length : b.initialSigns.length;
        const bbText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        bbText.setAttribute('x', midPos.x);
        bbText.setAttribute('y', bbY);
        bbText.setAttribute('id', `signsText${b.id}`);
        bbText.classList.add('signs-billboard-text');
        bbText.textContent = `🪧 Signs: ${remCount}/${b.initialSigns.length}`;
        signsGroup.appendChild(bbText);
      });
    }

    // 8. Stop Signs
    const stopSigns = isClashObstaclesActive ? (obstacleConfigs.stopSigns || ObstacleGeometry.getStopSignConfigs(settings)) : [];
    const isStopSignActive = isClashObstaclesActive && Boolean(settings.optStopSign || settings.optStopSigns || (isClashTarget && settings.optStopSign !== false && settings.optStopSigns !== false));
    if (isClashObstaclesActive && isStopSignActive && stopSignsGroup && Array.isArray(stopSigns)) {
      stopSigns.forEach(s => {
        if (s.hole > targetHoles) return;
        const pt = getTrackXY(s.hole, refTrack);
        const isTopTrack = pt.y < (isClashTarget ? (curCfg.viewH / 2) : BOARD_CFG.st2Y);
        const signY = pt.y + (isTopTrack ? -36 : 36);
        const signX = pt.x;

        const pole = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        pole.setAttribute('x1', pt.x);
        pole.setAttribute('y1', pt.y);
        pole.setAttribute('x2', signX);
        pole.setAttribute('y2', signY);
        pole.setAttribute('stroke', '#94a3b8');
        pole.setAttribute('stroke-width', '3.2');
        pole.style.pointerEvents = 'none';
        stopSignsGroup.appendChild(pole);

        const r = 28; // 4x bigger (was 7.5)
        const points = [];
        for (let i = 0; i < 8; i++) {
          const angle = (i * Math.PI / 4) + (Math.PI / 8);
          points.push(`${(signX + r * Math.cos(angle)).toFixed(1)},${(signY + r * Math.sin(angle)).toFixed(1)}`);
        }
        const oct = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        oct.setAttribute('points', points.join(' '));
        oct.classList.add('board-stop-sign');
        oct.dataset.obstacle = 'stop-sign';
        oct.dataset.hole = String(s.hole);
        stopSignsGroup.appendChild(oct);

        const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        txt.setAttribute('x', signX);
        txt.setAttribute('y', signY + 1.8);
        txt.setAttribute('text-anchor', 'middle');
        txt.setAttribute('dominant-baseline', 'central');
        txt.setAttribute('fill', '#ffffff');
        txt.setAttribute('font-size', '15px');
        txt.setAttribute('font-weight', '900');
        txt.setAttribute('letter-spacing', '1.2px');
        txt.style.pointerEvents = 'none';
        txt.textContent = 'STOP';
        stopSignsGroup.appendChild(txt);
      });
    }

    // 8b. Dice Challenge Spaces
    const diceList = isClashObstaclesActive ? (obstacleConfigs.dice || ObstacleGeometry.getDiceConfigs(settings)) : [];
    if (isClashObstaclesActive && settings.optDice && diceGroup && Array.isArray(diceList)) {
      diceList.forEach(d => {
        if (d.hole > targetHoles) return;
        const pt = getTrackXY(d.hole, refTrack);
        const isTopTrack = pt.y < (isClashTarget ? (curCfg.viewH / 2) : BOARD_CFG.st2Y);
        const diceY = pt.y + (isTopTrack ? -16 : 16);
        const diceX = pt.x;

        const diceMarker = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        diceMarker.setAttribute('x', diceX);
        diceMarker.setAttribute('y', diceY);
        diceMarker.setAttribute('text-anchor', 'middle');
        diceMarker.setAttribute('dominant-baseline', 'central');
        diceMarker.setAttribute('font-size', '14px');
        diceMarker.setAttribute('filter', 'url(#pegShadow)');
        diceMarker.classList.add('dice-board-marker');
        diceMarker.dataset.obstacle = 'dice-box';
        diceMarker.dataset.hole = String(d.hole);
        diceMarker.style.cursor = 'pointer';
        diceMarker.textContent = '🎲';
        diceGroup.appendChild(diceMarker);
      });
    }

    // 8c. Spinning Wheel Challenge Spaces
    const wheelList = isClashObstaclesActive ? (obstacleConfigs.wheels || ObstacleGeometry.getWheelConfigs(settings)) : [];
    if (isClashObstaclesActive && (settings.optWheel || settings.optSpinningWheel) && wheelsGroup && Array.isArray(wheelList)) {
      wheelList.forEach(w => {
        if (w.hole > targetHoles) return;
        const pt = getTrackXY(w.hole, refTrack);
        const isTopTrack = pt.y < (isClashTarget ? (curCfg.viewH / 2) : BOARD_CFG.st2Y);
        const wheelY = pt.y + (isTopTrack ? -16 : 16);
        const wheelX = pt.x;

        const wheelMarker = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        wheelMarker.setAttribute('x', wheelX);
        wheelMarker.setAttribute('y', wheelY);
        wheelMarker.setAttribute('text-anchor', 'middle');
        wheelMarker.setAttribute('dominant-baseline', 'central');
        wheelMarker.setAttribute('font-size', '14px');
        wheelMarker.setAttribute('filter', 'url(#pegShadow)');
        wheelMarker.classList.add('wheel-board-marker');
        wheelMarker.dataset.obstacle = 'wheel-box';
        wheelMarker.dataset.hole = String(w.hole);
        wheelMarker.style.cursor = 'pointer';
        wheelMarker.textContent = '🎡';
        wheelsGroup.appendChild(wheelMarker);
      });
    }

    // 9. Skunk Markers
    if (settings.optSkunkMarkers && skunkMarkersGroup) {
      const sDefs = BoardRenderer.getSkunkDefaults(settings.target || 481);
      const markers = [
        {
          hole: settings.skunkMarkerHole || sDefs.skunkHole,
          label: 'SKUNK',
          icon: '🦨',
          dashColor: '#38bdf8',
          borderColor: '#38bdf8',
          bgColor: '#090d16',
          textColor: '#ffffff',
          title: 'Skunk Line'
        },
        {
          hole: settings.doubleSkunkMarkerHole || sDefs.doubleSkunkHole,
          label: 'DBL SKUNK',
          icon: '🦨',
          dashColor: '#f59e0b',
          borderColor: '#f59e0b',
          bgColor: '#090d16',
          textColor: '#ffffff',
          title: 'Double Skunk Line'
        }
      ];

      markers.forEach(m => {
        if (m.hole <= 0 || m.hole > targetHoles) return;
        const ptFirst = getTrackXY(m.hole, activeTracks[0]);
        const ptLast = getTrackXY(m.hole, activeTracks[activeTracks.length - 1]);

        const nextH = Math.min(targetHoles, m.hole + 1);
        const prevH = Math.max(1, m.hole - 1);
        const pNext = getTrackXY(nextH, activeTracks[0]);
        const pPrev = getTrackXY(prevH, activeTracks[0]);
        const tdx = pNext.x - pPrev.x;
        const tdy = pNext.y - pPrev.y;
        const tLen = Math.hypot(tdx, tdy) || 1;
        const tx = tdx / tLen;
        const ty = tdy / tLen;

        let nx, ny;
        if (dist > 0.1) {
          nx = dx / dist;
          ny = dy / dist;
        } else {
          nx = -ty;
          ny = tx;
        }

        const ext = 7;
        const x1 = ptFirst.x - nx * ext;
        const y1 = ptFirst.y - ny * ext;
        const x2 = ptLast.x + nx * ext;
        const y2 = ptLast.y + ny * ext;

        const shadowLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        shadowLine.setAttribute('x1', x1);
        shadowLine.setAttribute('y1', y1);
        shadowLine.setAttribute('x2', x2);
        shadowLine.setAttribute('y2', y2);
        shadowLine.setAttribute('stroke', '#000000');
        shadowLine.setAttribute('stroke-width', '4');
        shadowLine.setAttribute('stroke-linecap', 'round');
        shadowLine.setAttribute('opacity', '0.75');
        shadowLine.style.pointerEvents = 'none';
        skunkMarkersGroup.appendChild(shadowLine);

        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', x1);
        line.setAttribute('y1', y1);
        line.setAttribute('x2', x2);
        line.setAttribute('y2', y2);
        line.setAttribute('stroke', m.dashColor);
        line.setAttribute('stroke-width', '2.5');
        line.setAttribute('stroke-linecap', 'round');
        line.setAttribute('stroke-dasharray', '4,2');
        line.setAttribute('opacity', '1');
        line.style.pointerEvents = 'none';
        skunkMarkersGroup.appendChild(line);

        // Draw skunk(s) on each side of the track (doubled size):
        // Single skunk: 1 skunk on each side
        // Double skunk: 2 skunks on each side
        const isDouble = m.label === 'DBL SKUNK';
        const sideOffset = ext + (isDouble ? 20 : 18);
        const sides = [
          { x: ptFirst.x - nx * sideOffset, y: ptFirst.y - ny * sideOffset },
          { x: ptLast.x + nx * sideOffset, y: ptLast.y + ny * sideOffset }
        ];

        sides.forEach(sidePt => {
          const sideGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          sideGroup.classList.add('skunk-board-marker');
          sideGroup.dataset.obstacle = 'skunk-marker';
          sideGroup.dataset.hole = String(m.hole);
          sideGroup.style.cursor = 'pointer';

          const tip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
          tip.textContent = `${m.title} (Hole ${m.hole})`;
          sideGroup.appendChild(tip);

          if (!isDouble) {
            // Single Skunk Marker: 1 skunk on each side (doubled size: r=15, font 19px)
            const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
            bgCircle.setAttribute('cx', sidePt.x);
            bgCircle.setAttribute('cy', sidePt.y);
            bgCircle.setAttribute('r', '15');
            bgCircle.setAttribute('fill', m.bgColor);
            bgCircle.setAttribute('stroke', m.borderColor);
            bgCircle.setAttribute('stroke-width', '2.0');
            bgCircle.setAttribute('filter', 'url(#pegShadow)');
            sideGroup.appendChild(bgCircle);

            const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            txt.setAttribute('x', sidePt.x);
            txt.setAttribute('y', sidePt.y + 1.2);
            txt.setAttribute('text-anchor', 'middle');
            txt.setAttribute('dominant-baseline', 'central');
            txt.setAttribute('font-size', '19px');
            txt.style.pointerEvents = 'none';
            txt.textContent = '🦨';
            sideGroup.appendChild(txt);
          } else {
            // Double Skunk Marker: 2 skunks on each side (doubled size: 52x30 pill, font 18px)
            const pillAngle = Math.atan2(ty, tx) * 180 / Math.PI;
            const pillWrap = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            pillWrap.setAttribute('transform', `translate(${sidePt.x}, ${sidePt.y}) rotate(${pillAngle})`);

            const pillRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            pillRect.setAttribute('x', '-26');
            pillRect.setAttribute('y', '-15');
            pillRect.setAttribute('width', '52');
            pillRect.setAttribute('height', '30');
            pillRect.setAttribute('rx', '15');
            pillRect.setAttribute('fill', m.bgColor);
            pillRect.setAttribute('stroke', m.borderColor);
            pillRect.setAttribute('stroke-width', '2.0');
            pillRect.setAttribute('filter', 'url(#pegShadow)');
            pillWrap.appendChild(pillRect);

            const txt1 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            txt1.setAttribute('x', '-11');
            txt1.setAttribute('y', '1.0');
            txt1.setAttribute('text-anchor', 'middle');
            txt1.setAttribute('dominant-baseline', 'central');
            txt1.setAttribute('font-size', '18px');
            txt1.style.pointerEvents = 'none';
            txt1.textContent = '🦨';
            pillWrap.appendChild(txt1);

            const txt2 = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            txt2.setAttribute('x', '11');
            txt2.setAttribute('y', '1.0');
            txt2.setAttribute('text-anchor', 'middle');
            txt2.setAttribute('dominant-baseline', 'central');
            txt2.setAttribute('font-size', '18px');
            txt2.style.pointerEvents = 'none';
            txt2.textContent = '🦨';
            pillWrap.appendChild(txt2);

            sideGroup.appendChild(pillWrap);
          }

          skunkMarkersGroup.appendChild(sideGroup);
        });
      });
    }

    // 10. Track Holes
    const noPassingLanes = obstacleConfigs.noPassingLanes || ObstacleGeometry.getNoPassingLanes(settings);
    const isInNoPassingLane = h => noPassingLanes.some(l => h >= l.start && h <= l.end);

    const isInPegThroughZone = h => (h >= 31 && h <= 40) || (h >= 61 && h <= 70) || (h >= 91 && h <= 100);
    const isInLast5PegOut = h => h >= (targetHoles - 5) && h < targetHoles;
    const isInBunker = h => (h >= 1 && h <= 5) || (h >= (targetHoles - 5) && h < targetHoles) || ([121, 241, 361, 481].includes(targetHoles) && ((h >= 50 && h <= 60) || (h >= 80 && h <= 90)));

    for (const who of activeTracks) {
      for (let h = 0; h <= targetHoles; h++) {
        const inNoPassing = isClashObstaclesActive && settings.optNoPassingLanes && isInNoPassingLane(h);
        if (inNoPassing && who !== activeTracks[0]) continue;

        const pos = getTrackXY(h, who);
        const isMilestone = h > 0 && h % 5 === 0;
        const isGameHole = h === targetHoles;
        const jump = isClashObstaclesActive && settings.optJump && jumps.find(j => j.start === h);
        const isJumpDest = isClashObstaclesActive && settings.optJump && jumps.some(j => j.dest === h);
        const isJailTrap = isClashObstaclesActive && settings.optJail && jails.some(j => j.hole === h);
        const isBarTrap = isClashObstaclesActive && settings.optBar && bars.some(b => b.hole === h);
        const isTimeoutTrap = isClashObstaclesActive && settings.optTimeout && timeouts.some(t => t.hole === h);
        const isDetourTrap = isClashObstaclesActive && settings.optDetour && detours.some(d => d.hole === h);
        const isRoundAboutTrap = isClashObstaclesActive && settings.optRoundAbout && roundabouts.some(r => r.hole === h);
        const signBlock = isClashObstaclesActive && settings.optSigns && signs.find(b => h >= b.start && h <= b.end);
        const isStopSignTrap = isClashObstaclesActive && settings.optStopSign && stopSigns.some(s => s.hole === h);
        const isDiceTrap = isClashObstaclesActive && settings.optDice && Array.isArray(diceList) && diceList.some(d => d.hole === h);
        const isWheelTrap = isClashObstaclesActive && (settings.optWheel || settings.optSpinningWheel) && Array.isArray(wheelList) && wheelList.some(w => w.hole === h);

        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', pos.x);
        circle.setAttribute('cy', pos.y);
        circle.setAttribute('r', isGameHole ? '4.8' : (inNoPassing ? (isMilestone ? '3.0' : '2.4') : (isMilestone ? '2.8' : '2')));
        circle.setAttribute('fill', isGameHole ? '#f0c64a' : 'url(#holeGrad)');
        circle.setAttribute('stroke', isGameHole ? '#4a2c04' : (isMilestone ? '#3a1b07' : '#1a0b03'));
        circle.setAttribute('stroke-width', isMilestone || isGameHole ? '1.2' : '0.8');
        circle.dataset.hole = String(h);
        circle.dataset.who = who;

        if (isClashObstaclesActive) {
          if (inNoPassing && !isGameHole) {
            circle.classList.add('hole-no-passing-lane');
          } else if (isStopSignTrap && !isGameHole) {
            circle.classList.add('hole-stop-sign');
          } else if (isDiceTrap && !isGameHole) {
            circle.classList.add('hole-dice-trigger');
          } else if (isWheelTrap && !isGameHole) {
            circle.classList.add('hole-wheel-trigger');
          } else if (isJailTrap && !isGameHole) {
            circle.classList.add('hole-jail-trigger');
          } else if (isBarTrap && !isGameHole) {
            circle.classList.add('hole-bar-trigger');
          } else if (isTimeoutTrap && !isGameHole) {
            circle.classList.add('hole-timeout-trigger');
          } else if (isDetourTrap && !isGameHole) {
            circle.classList.add('hole-detour-trigger');
          } else if (isRoundAboutTrap && !isGameHole) {
            circle.classList.add('hole-roundabout-trigger');
          } else if (signBlock && !isGameHole) {
            circle.classList.add('hole-sign-block');
          } else if (jump && !isGameHole) {
            circle.classList.add(jump.isForward ? 'hole-jump-trigger-forward' : 'hole-jump-trigger-backward');
          } else if (isJumpDest && !isGameHole) {
            circle.classList.add('hole-jump-dest');
          }
        } else if (brand === 'crazy') {
          if (settings.optPegThroughZones && isInPegThroughZone(h) && !isGameHole) {
            circle.classList.add('hole-peg-zone');
          } else if (settings.optLast5PegOut && isInLast5PegOut(h) && !isGameHole) {
            circle.classList.add('hole-last5-zone');
          } else if (settings.optBunkers && isInBunker(h) && !isGameHole) {
            circle.classList.add('hole-bunker-zone');
          }
        }

        if (brand === 'pints' && !isGameHole) {
          const pintsKeys = ['youDrink', 'guysDrink', 'galsDrink', 'leftDrink', 'rightDrink', 'everyoneDrink', 'everyoneButYouDrink', 'pickWhoDrinks'];
          for (const pk of pintsKeys) {
            const chk = settings[`opt${pk.charAt(0).toUpperCase() + pk.slice(1)}`];
            if (chk) {
              const count = settings[`${pk}Count`] || settings[`opt${pk.charAt(0).toUpperCase() + pk.slice(1)}Count`] || 2;
              let isMatch = false;
              for (let ci = 1; ci <= count; ci++) {
                const hVal = settings[`opt${pk.charAt(0).toUpperCase() + pk.slice(1)}${ci}Hole`] || (settings[`${pk}Holes`] && settings[`${pk}Holes`][ci - 1]);
                if (hVal === h) {
                  isMatch = true;
                  break;
                }
              }
              if (isMatch) {
                circle.classList.add(`hole-${pk.replace(/([A-Z])/g, '-$1').toLowerCase()}`);
                break;
              }
            }
          }
        } else if (brand === 'adult' && !isGameHole) {
          const adultKeys = ['loseClothing', 'gainClothing', 'pickWhoLosesClothing', 'rightLosesClothing', 'leftLosesClothing', 'removeClothingChoice', 'touchBreast', 'lickBreast', 'suckBreast', 'touchGenitals', 'lickGenitals', 'suckGenitals', 'fingerGenitals'];
          for (const ak of adultKeys) {
            const chk = settings[`opt${ak.charAt(0).toUpperCase() + ak.slice(1)}`];
            if (chk) {
              const count = settings[`${ak}Count`] || settings[`opt${ak.charAt(0).toUpperCase() + ak.slice(1)}Count`] || 2;
              let isMatch = false;
              for (let ci = 1; ci <= count; ci++) {
                const hVal = settings[`opt${ak.charAt(0).toUpperCase() + ak.slice(1)}${ci}Hole`] || (settings[`${ak}Holes`] && settings[`${ak}Holes`][ci - 1]);
                if (hVal === h) {
                  isMatch = true;
                  break;
                }
              }
              if (isMatch) {
                circle.classList.add('hole-strip-action');
                circle.classList.add(`hole-${ak.replace(/([A-Z])/g, '-$1').toLowerCase()}`);
                circle.setAttribute('data-adult-key', ak);
                break;
              }
            }
          }
        }
        holesGroup.appendChild(circle);

        // Milestone hash tick lines
        const isTurn = isClashTarget ? (h > 0 && h < targetHoles && ((h - 1) % 60) >= 55) : ((h >= 31 && h <= 35) || (h >= 66 && h <= 70));
        if (who === activeTracks[0] && activeTracks.length > 1 && isMilestone && !isTurn && h < targetHoles && !inNoPassing) {
          const pt1 = getTrackXY(h, activeTracks[0]);
          const pt4 = getTrackXY(h, activeTracks[activeTracks.length - 1]);
          const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
          line.setAttribute('x1', pt1.x);
          line.setAttribute('y1', pt1.y + (pt1.y < pt4.y ? 2.6 : -2.6));
          line.setAttribute('x2', pt4.x);
          line.setAttribute('y2', pt4.y + (pt1.y < pt4.y ? -2.6 : 2.6));
          line.setAttribute('stroke', '#6d421a');
          line.setAttribute('stroke-width', '1');
          line.setAttribute('stroke-opacity', '0.65');
          holesGroup.appendChild(line);
        }
      }
    }

    // 11. Clash board labels
    if (isClashTarget) {
      const p0 = getTrackXY(0, activeTracks[0]);
      const startTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      startTxt.setAttribute('x', p0.x);
      startTxt.setAttribute('y', p0.y - 12);
      startTxt.setAttribute('fill', '#4a2c04');
      startTxt.setAttribute('font-size', '9');
      startTxt.setAttribute('font-weight', '800');
      startTxt.setAttribute('text-anchor', 'middle');
      startTxt.setAttribute('letter-spacing', '0.05em');
      startTxt.textContent = 'START';
      holesGroup.appendChild(startTxt);

      const pFinish = getTrackXY(targetHoles, activeTracks[0]);
      const gameTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      gameTxt.setAttribute('x', pFinish.x + 16);
      gameTxt.setAttribute('y', pFinish.y + 3.5);
      gameTxt.setAttribute('fill', '#4a2c04');
      gameTxt.setAttribute('font-size', '10');
      gameTxt.setAttribute('font-weight', '800');
      gameTxt.setAttribute('letter-spacing', '0.04em');
      gameTxt.textContent = `★ ${targetHoles} GAME`;
      holesGroup.appendChild(gameTxt);

      for (let r = 0; r < curCfg.numRows; r++) {
        const isEven = r % 2 === 0;
        const stTxt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        stTxt.setAttribute('x', isEven ? 32 : 788);
        stTxt.setAttribute('y', curCfg.stY[r] + 3.5);
        stTxt.setAttribute('fill', '#5c3814');
        stTxt.setAttribute('font-size', '8.5');
        stTxt.setAttribute('font-weight', '700');
        stTxt.setAttribute('opacity', '0.85');
        stTxt.setAttribute('text-anchor', 'middle');
        stTxt.textContent = `ST ${r + 1}`;
        holesGroup.appendChild(stTxt);
      }
    }

    // 12. Pegs layer
    const pegsGroup = svg.querySelector('#pegsLayer');
    activeTracks.forEach(who => {
      ['rear', 'front'].forEach(role => {
        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.id = `peg-${who}-${role}`;
        g.className.baseVal = 'peg-group';
        g.setAttribute('filter', 'url(#pegShadow)');
        g.dataset.pegWho = who;
        g.dataset.pegRole = role;

        g.innerHTML = BoardRenderer.buildPegSvgContent(who, role, settings);

        // Click handler on Player 1 peg to open avatar picker
        if (who === 'p1' || who === 'you' || who === 'team1') {
          g.style.cursor = 'pointer';
          g.setAttribute('title', 'Your Peg — Click to customize your avatar persona');
          g.addEventListener('click', (e) => {
            e.stopPropagation();
            const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
            if (avMgr && typeof avMgr.openAvatarModal === 'function') {
              avMgr.openAvatarModal();
            }
          });
        }
        pegsGroup.appendChild(g);
      });

      if (!pegState[who]) pegState[who] = { front: 0, rear: 0 };
      pegState[who].front = (state && state.scores && state.scores[who]) || 0;
      pegState[who].rear = (state && state.prev && state.prev[who]) || 0;
    });

    // 13. Interactive Legend
    const hasDrinking = settings && (settings.optYouDrink || settings.optGuysDrink || settings.optGalsDrink || settings.optLeftDrink || settings.optRightDrink || settings.optEveryoneDrink || settings.optEveryoneButYouDrink || settings.optPickWhoDrinks);
    if (isClashTarget || brand === 'pints' || brand === 'adult' || hasDrinking) {
      const legendEl = (typeof createLegendFn === 'function')
        ? createLegendFn()
        : BoardRenderer.createLegend({ brand, settings, adultOptions: adultConfigs, obstacleConfigs });
      if (legendEl) wrap.appendChild(legendEl);
    }

    // 14. After render hook
    if (typeof onAfterRender === 'function') {
      onAfterRender();
    }
    BoardRenderer.updateBoardSizeButtons();

    return wrap;
  }

  /**
   * Synchronizes SVG front and rear peg markers with current player/team scores.
   * Handles smooth leap transitions and obstacle coordinates.
   * @param {boolean} [animate=true]
   * @param {Object} [context={}]
   */
  static renderScores(animate = true, context = {}) {
    if (typeof document === 'undefined') return;

    const state = context.state || (typeof window !== 'undefined' ? window.state : null);
    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : {});
    const pegState = context.pegState || (typeof window !== 'undefined' ? window.pegState : {});
    const getActiveTracks = context.getActiveTracks || (typeof window !== 'undefined' ? window.getActiveTracks : (() => ['p1', 'p2']));
    const baseGetTrackXY = context.getTrackXY || (typeof window !== 'undefined' && window.getTrackXY) || defaultGetTrackXY;
    const getTrackXY = (h, w) => (typeof baseGetTrackXY === 'function' ? baseGetTrackXY(h, w, t, settings) : defaultGetTrackXY(h, w, t, settings));

    const getJailStepXY = context.getJailStepXY || (typeof window !== 'undefined' ? window.getJailStepXY : ObstacleGeometry.getJailStepXY);
    const getBarStepXY = context.getBarStepXY || (typeof window !== 'undefined' ? window.getBarStepXY : ObstacleGeometry.getBarStepXY);
    const getTimeoutStepXY = context.getTimeoutStepXY || (typeof window !== 'undefined' ? window.getTimeoutStepXY : ObstacleGeometry.getTimeoutStepXY);
    const getDetourStepXY = context.getDetourStepXY || (typeof window !== 'undefined' ? window.getDetourStepXY : ObstacleGeometry.getDetourStepXY);
    const getRoundAboutStepXY = context.getRoundAboutStepXY || (typeof window !== 'undefined' ? window.getRoundAboutStepXY : ObstacleGeometry.getRoundAboutStepXY);

    if (typeof context.updateScoreChips === 'function') {
      context.updateScoreChips();
    } else if (typeof window !== 'undefined' && typeof window.updateScoreChips === 'function') {
      window.updateScoreChips();
    }

    try {
      const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
      if (avMgr && typeof avMgr.syncAvatarUI === 'function') {
        avMgr.syncAvatarUI();
      }
    } catch (e) {}

    const t = settings.target || 121;
    const activeTracks = getActiveTracks();

    // Phase 2: Ensure all active tracks have their avatar pattern in board defs
    try {
      const boardSvg = document.getElementById('serpentineBoard');
      const defsEl = boardSvg ? boardSvg.querySelector('defs') : null;
      if (defsEl) {
        activeTracks.forEach(who => {
          const patId = `pegAvatarPattern_${who}`;
          if (!defsEl.querySelector(`#${patId}`)) {
            const avId = BoardRenderer.getTrackAvatarId(who, settings);
            const rawSvg = AvatarManager.getAvatarPatternSvg(avId, patId);
            const parser = new DOMParser();
            const doc = parser.parseFromString(`<svg xmlns="http://www.w3.org/2000/svg">${rawSvg}</svg>`, 'image/svg+xml');
            const patNode = doc.querySelector('pattern');
            if (patNode) {
              defsEl.appendChild(document.importNode(patNode, true));
            }
          }
        });
      }
    } catch (_) {}

    const r3d = (typeof window !== 'undefined') ? (window._currentRenderer3D || window.boardRenderer3D) : null;
    if (r3d && (!settings || (settings.boardViewMode !== '2d' && settings.boardViewMode !== 'none'))) {
      r3d.updatePegs(state?.scores || {}, state?.prev || {}, animate, state?.lastScoringEvent || null);
      if (typeof r3d.syncFromGameState === 'function') {
        r3d.syncFromGameState({ state, settings });
      }
    }

    activeTracks.forEach(who => {
      const is2P = settings && settings.playerCount === 2;
      const scoreTargetId = (who === 'p1' && is2P) ? 'you' : ((who === 'p2' && is2P) ? 'cpu' : who);
      const scoreEl = document.getElementById(`${scoreTargetId}Score`) || document.getElementById(`${who}Score`) || (who === 'p1' ? document.getElementById('youScore') : (who === 'p2' ? document.getElementById('cpuScore') : null));
      const sc = (state && state.scores && (state.scores[who] !== undefined ? state.scores[who] : (state.scores[scoreTargetId] !== undefined ? state.scores[scoreTargetId] : 0))) || 0;
      if (scoreEl) scoreEl.textContent = sc;

      const currentScore = Math.min(t, sc);
      const prevSc = (state && state.prev && (state.prev[who] !== undefined ? state.prev[who] : (state.prev[scoreTargetId] !== undefined ? state.prev[scoreTargetId] : 0))) || 0;
      const prevScore = Math.min(t, prevSc);

      if (!pegState[who]) pegState[who] = { front: 0, rear: 0 };

      const fEl = document.getElementById(`peg-${who}-front`);
      const rEl = document.getElementById(`peg-${who}-rear`);
      if (!fEl || !rEl) return;

      const isJailed = settings.optJail && state && state.jailed && state.jailed[who];
      const jailedStep = isJailed ? state.jailed[who].step : -1;
      if (pegState[who]._lastJailStep === undefined) pegState[who]._lastJailStep = -1;
      const jailStepChanged = isJailed && (pegState[who]._lastJailStep !== jailedStep);

      const isAtBar = settings.optBar && state && state.atBar && state.atBar[who];
      const barStep = isAtBar ? state.atBar[who].step : -1;
      if (pegState[who]._lastBarStep === undefined) pegState[who]._lastBarStep = -1;
      const barStepChanged = isAtBar && (pegState[who]._lastBarStep !== barStep);

      const isInTimeout = settings.optTimeout && state && state.inTimeout && state.inTimeout[who];
      const timeoutStep = isInTimeout ? state.inTimeout[who].step : -1;
      if (pegState[who]._lastTimeoutStep === undefined) pegState[who]._lastTimeoutStep = -1;
      const timeoutStepChanged = isInTimeout && (pegState[who]._lastTimeoutStep !== timeoutStep);

      const isInDetour = settings.optDetour && state && state.inDetour && state.inDetour[who];
      const detourProgress = isInDetour ? state.inDetour[who].progress : -1;
      if (pegState[who]._lastDetourProgress === undefined) pegState[who]._lastDetourProgress = -1;
      const detourProgressChanged = isInDetour && (pegState[who]._lastDetourProgress !== detourProgress);

      const isInRoundAbout = settings.optRoundAbout && state && state.inRoundAbout && state.inRoundAbout[who];
      const roundAboutProgress = isInRoundAbout ? state.inRoundAbout[who].progress : -1;
      if (pegState[who]._lastRoundAboutProgress === undefined) pegState[who]._lastRoundAboutProgress = -1;
      const roundAboutProgressChanged = isInRoundAbout && (pegState[who]._lastRoundAboutProgress !== roundAboutProgress);

      const oldFront = pegState[who].front;
      const isBackward = currentScore < oldFront && !isJailed && !isAtBar && !isInTimeout && !isInDetour && !isInRoundAbout;

      if (animate && (currentScore !== pegState[who].front || jailStepChanged || barStepChanged || timeoutStepChanged || detourProgressChanged || roundAboutProgressChanged)) {
        if (pegState[who]._rearTimer) {
          clearTimeout(pegState[who]._rearTimer);
          pegState[who]._rearTimer = null;
        }
        if (pegState[who]._leapTimer) {
          clearTimeout(pegState[who]._leapTimer);
          pegState[who]._leapTimer = null;
        }
        if (pegState[who]._rearLeapTimer) {
          clearTimeout(pegState[who]._rearLeapTimer);
          pegState[who]._rearLeapTimer = null;
        }
        if (pegState[who]._hopTimers) {
          pegState[who]._hopTimers.forEach(t => clearTimeout(t));
          pegState[who]._hopTimers = null;
        }
        fEl.classList.remove('peg-hopping');

        pegState[who]._lastJailStep = jailedStep;
        pegState[who]._lastBarStep = barStep;
        pegState[who]._lastTimeoutStep = timeoutStep;
        pegState[who]._lastDetourProgress = detourProgress;
        pegState[who]._lastRoundAboutProgress = roundAboutProgress;

        if (isBackward) {
          pegState[who].front = currentScore;
          const newRear = Math.max(0, currentScore - 1);
          pegState[who].rear = newRear;
          if (state && state.prev) {
            state.prev[who] = newRear;
            if (who === 'p1' || who === 'team1') state.prev.you = newRear;
            if (who === 'p2' || who === 'team2') state.prev.cpu = newRear;
          }

          const newFrontPos = getTrackXY(currentScore, who);
          const newRearPos = getTrackXY(newRear, who);

          fEl.classList.add('leaping');
          fEl.setAttribute('transform', `translate(${newFrontPos.x}, ${newFrontPos.y})`);

          pegState[who]._leapTimer = setTimeout(() => {
            fEl.classList.remove('leaping');
            pegState[who]._leapTimer = null;
          }, 440);

          pegState[who]._rearTimer = setTimeout(() => {
            rEl.classList.add('leaping');
            rEl.setAttribute('transform', `translate(${newRearPos.x}, ${newRearPos.y})`);
            pegState[who]._rearLeapTimer = setTimeout(() => {
              rEl.classList.remove('leaping');
              pegState[who]._rearLeapTimer = null;
            }, 440);
            pegState[who]._rearTimer = null;
          }, 460);
        } else {
          const oldScore = oldFront;
          const scoreDelta = currentScore - oldScore;
          pegState[who].rear = oldScore;
          pegState[who].front = currentScore;

          let newFrontPos = getTrackXY(pegState[who].front, who);
          if (isJailed && typeof getJailStepXY === 'function') {
            newFrontPos = getJailStepXY(state.jailed[who].jailHole, state.jailed[who].step, who);
          } else if (isAtBar && typeof getBarStepXY === 'function') {
            newFrontPos = getBarStepXY(state.atBar[who].barHole, state.atBar[who].step, who);
          } else if (isInTimeout && typeof getTimeoutStepXY === 'function') {
            newFrontPos = getTimeoutStepXY(state.inTimeout[who].timeoutHole, state.inTimeout[who].step, who);
          } else if (isInDetour && typeof getDetourStepXY === 'function') {
            newFrontPos = getDetourStepXY(state.inDetour[who].detourHole, state.inDetour[who].progress, who);
          } else if (isInRoundAbout && typeof getRoundAboutStepXY === 'function') {
            newFrontPos = getRoundAboutStepXY(state.inRoundAbout[who].roundAboutHole, state.inRoundAbout[who].progress, who);
          }
          const newRearPos = getTrackXY(pegState[who].rear, who);
          rEl.setAttribute('transform', `translate(${newRearPos.x}, ${newRearPos.y})`);

          const isSpecialZone = isJailed || isAtBar || isInTimeout || isInDetour || isInRoundAbout;
          const sfx = context.sfx || (typeof window !== 'undefined' ? window.sfx : null);

          if (!isSpecialZone && scoreDelta >= 1 && scoreDelta <= 14) {
            fEl.classList.add('peg-hopping');
            pegState[who]._hopTimers = [];
            const hopDelay = Math.max(45, Math.min(75, Math.floor(620 / scoreDelta)));

            for (let step = 1; step <= scoreDelta; step++) {
              const h = oldScore + step;
              const timer = setTimeout(() => {
                const stepPos = getTrackXY(h, who);
                const isLast = (step === scoreDelta);
                fEl.setAttribute('transform', `translate(${stepPos.x}, ${stepPos.y})`);
                if (sfx && typeof sfx.thunk === 'function') {
                  const pitchMod = 0.95 + (step / scoreDelta) * 0.15;
                  sfx.thunk(pitchMod);
                } else if (sfx && typeof sfx.clack === 'function') {
                  const pitchMod = 0.95 + (step / scoreDelta) * 0.15;
                  sfx.clack(pitchMod);
                }
                if (isLast) {
                  fEl.classList.remove('peg-hopping');
                  pegState[who]._hopTimers = null;
                }
              }, (step - 1) * hopDelay);
              pegState[who]._hopTimers.push(timer);
            }
          } else {
            fEl.classList.add('leaping');
            fEl.setAttribute('transform', `translate(${newFrontPos.x}, ${newFrontPos.y})`);
            const playPegSound = (pMod = 1.0) => {
              if (!sfx) return;
              if (typeof sfx.thunk === 'function') sfx.thunk(pMod);
              else if (typeof sfx.clack === 'function') sfx.clack(pMod);
            };
            playPegSound(1.0);
            if (scoreDelta > 14) {
              setTimeout(() => playPegSound(1.06), 70);
              setTimeout(() => playPegSound(1.12), 140);
              setTimeout(() => playPegSound(1.18), 210);
            }
            pegState[who]._leapTimer = setTimeout(() => {
              fEl.classList.remove('leaping');
              pegState[who]._leapTimer = null;
            }, 450);
          }
        }
      } else {
        if (pegState[who]._hopTimers) {
          pegState[who]._hopTimers.forEach(t => clearTimeout(t));
          pegState[who]._hopTimers = null;
        }
        fEl.classList.remove('peg-hopping');
        fEl.classList.remove('leaping');
        let fPos = getTrackXY(currentScore, who);
        if (isJailed && typeof getJailStepXY === 'function') {
          fPos = getJailStepXY(state.jailed[who].jailHole, state.jailed[who].step, who);
        } else if (isAtBar && typeof getBarStepXY === 'function') {
          fPos = getBarStepXY(state.atBar[who].barHole, state.atBar[who].step, who);
        } else if (isInTimeout && typeof getTimeoutStepXY === 'function') {
          fPos = getTimeoutStepXY(state.inTimeout[who].timeoutHole, state.inTimeout[who].step, who);
        } else if (isInDetour && typeof getDetourStepXY === 'function') {
          fPos = getDetourStepXY(state.inDetour[who].detourHole, state.inDetour[who].progress, who);
        } else if (isInRoundAbout && typeof getRoundAboutStepXY === 'function') {
          fPos = getRoundAboutStepXY(state.inRoundAbout[who].roundAboutHole, state.inRoundAbout[who].progress, who);
        }
        const safeRear = (currentScore < prevScore) ? Math.max(0, currentScore - 1) : prevScore;
        const rPos = getTrackXY(safeRear, who);
        fEl.setAttribute('transform', `translate(${fPos.x}, ${fPos.y})`);
        rEl.setAttribute('transform', `translate(${rPos.x}, ${rPos.y})`);
        pegState[who].front = currentScore;
        pegState[who].rear = safeRear;
        pegState[who]._lastJailStep = jailedStep;
        pegState[who]._lastBarStep = barStep;
        pegState[who]._lastTimeoutStep = timeoutStep;
        pegState[who]._lastDetourProgress = detourProgress;
        pegState[who]._lastRoundAboutProgress = roundAboutProgress;
      }
    });

    if (typeof context.updateCoachDisplay === 'function') {
      context.updateCoachDisplay();
    } else if (typeof window !== 'undefined' && typeof window.updateCoachDisplay === 'function') {
      window.updateCoachDisplay();
    }
  }

  /**
   * Assembles runtime context dictionary required for board hover tooltips.
   * @param {Object} [deps]
   * @returns {Object}
   */
  static getBoardTooltipContext(deps = {}) {
    const win = typeof window !== 'undefined' ? window : {};
    const state = deps.state || win.state || null;
    const settings = deps.settings || win.settings || null;
    const pegState = deps.pegState || win.pegState || null;
    const brandFn = deps.getAppBrand || win.getAppBrand;
    const brand = typeof brandFn === 'function' ? brandFn() : (deps.brand || win.CRAZY_BRAND || 'crazy');
    const OG = ObstacleGeometry || win.CrazyCribbage?.ObstacleGeometry;
    return {
      state,
      settings,
      pegState,
      brand,
      getStreetOrTurnName: deps.getStreetOrTurnName || win.getStreetOrTurnName || (h => (OG && OG.getStreetOrTurnName ? OG.getStreetOrTurnName(h) : '')),
      getSignConfigs: deps.getSignConfigs || win.getSignConfigs || (() => (OG && OG.getSignConfigs ? OG.getSignConfigs(settings) : [])),
      getStopSignConfigs: deps.getStopSignConfigs || win.getStopSignConfigs || (() => (OG && OG.getStopSignConfigs ? OG.getStopSignConfigs(settings) : [])),
      getJailConfigs: deps.getJailConfigs || win.getJailConfigs || (() => (OG && OG.getJailConfigs ? OG.getJailConfigs(settings) : [])),
      getBarConfigs: deps.getBarConfigs || win.getBarConfigs || (() => (OG && OG.getBarConfigs ? OG.getBarConfigs(settings) : [])),
      getTimeoutConfigs: deps.getTimeoutConfigs || win.getTimeoutConfigs || (() => (OG && OG.getTimeoutConfigs ? OG.getTimeoutConfigs(settings) : [])),
      getJumpConfigs: deps.getJumpConfigs || win.getJumpConfigs || (() => (OG && OG.getJumpConfigs ? OG.getJumpConfigs(settings) : [])),
      getDetourConfigs: deps.getDetourConfigs || win.getDetourConfigs || (() => (OG && OG.getDetourConfigs ? OG.getDetourConfigs(settings) : [])),
      getRoundAboutConfigs: deps.getRoundAboutConfigs || win.getRoundAboutConfigs || (() => (OG && OG.getRoundAboutConfigs ? OG.getRoundAboutConfigs(settings) : [])),
      getNoPassingLanes: deps.getNoPassingLanes || win.getNoPassingLanes || (() => (OG && OG.getNoPassingLanes ? OG.getNoPassingLanes(settings) : [])),
      getDiceConfigs: deps.getDiceConfigs || win.getDiceConfigs || (() => (OG && OG.getDiceConfigs ? OG.getDiceConfigs(settings) : []))
    };
  }

  /**
   * Sets the board display state: 'normal', 'minimized', or 'maximized'.
   * @param {string} newState
   */
  static setBoardDisplayState(newState) {
    if (typeof document === 'undefined') return;
    const cribBoard = document.getElementById('cribBoard');
    if (!cribBoard) return;

    const validStates = ['normal', 'minimized', 'maximized'];
    const state = validStates.includes(newState) ? newState : 'normal';

    cribBoard.classList.toggle('is-minimized', state === 'minimized');
    cribBoard.classList.toggle('is-maximized', state === 'maximized');

    const tracks = document.getElementById('boardTracks');
    if (tracks) {
      tracks.style.display = state === 'minimized' ? 'none' : '';
    }

    const splitContainer = document.querySelector('.table.split');
    if (splitContainer) {
      splitContainer.classList.toggle('board-minimized', state === 'minimized');
      splitContainer.classList.toggle('board-maximized', state === 'maximized');
    }

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('cc_board_display_state', state);
      }
    } catch (_) {}

    BoardRenderer.updateBoardSizeButtons(state);

    if (typeof window !== 'undefined' && window._currentRenderer3D) {
      setTimeout(() => {
        if (window._currentRenderer3D?.handleResize) window._currentRenderer3D.handleResize();
        if (window._currentRenderer3D?.fitBoardToViewBox) window._currentRenderer3D.fitBoardToViewBox(true);
      }, 50);
      setTimeout(() => {
        if (window._currentRenderer3D?.handleResize) window._currentRenderer3D.handleResize();
        if (window._currentRenderer3D?.fitBoardToViewBox) window._currentRenderer3D.fitBoardToViewBox(true);
      }, 250);
    }
  }

  static getBoardDisplayState() {
    if (typeof document === 'undefined') return 'normal';
    const cribBoard = document.getElementById('cribBoard');
    if (!cribBoard) return 'normal';
    if (cribBoard.classList.contains('is-minimized')) return 'minimized';
    if (cribBoard.classList.contains('is-maximized')) return 'maximized';
    return 'normal';
  }

  static minimizeBoard() {
    BoardRenderer.setBoardDisplayState('minimized');
  }

  static maximizeBoard() {
    BoardRenderer.setBoardDisplayState('maximized');
  }

  static restoreBoard() {
    BoardRenderer.setBoardDisplayState('normal');
  }

  static toggleBoardMinimize() {
    if (BoardRenderer.getBoardDisplayState() === 'minimized') {
      BoardRenderer.restoreBoard();
    } else {
      BoardRenderer.minimizeBoard();
    }
  }

  static toggleBoardMaximize() {
    if (BoardRenderer.getBoardDisplayState() === 'maximized') {
      BoardRenderer.restoreBoard();
    } else {
      BoardRenderer.maximizeBoard();
    }
  }

  static updateBoardSizeButtons(state = BoardRenderer.getBoardDisplayState()) {
    if (typeof document === 'undefined') return;
    const btnMin = document.getElementById('btnMinimizeBoard');
    const btnMax = document.getElementById('btnMaximizeBoard');
    const iconMin = document.getElementById('btnMinimizeIcon');
    const iconMax = document.getElementById('btnMaximizeIcon');

    if (btnMin) {
      const isMin = state === 'minimized';
      btnMin.classList.toggle('active', isMin);
      btnMin.title = isMin ? 'Restore Normal Board' : 'Minimize Board (Collapse)';
      btnMin.setAttribute('aria-label', isMin ? 'Restore Normal Board' : 'Minimize Board (Collapse)');
      btnMin.setAttribute('aria-pressed', isMin ? 'true' : 'false');
      if (iconMin) iconMin.textContent = isMin ? '🗗' : '🗕';
    }

    if (btnMax) {
      const isMax = state === 'maximized';
      btnMax.classList.toggle('active', isMax);
      btnMax.title = isMax ? 'Restore Normal Board' : 'Maximize Board (Full View)';
      btnMax.setAttribute('aria-label', isMax ? 'Restore Normal Board' : 'Maximize Board (Full View)');
      btnMax.setAttribute('aria-pressed', isMax ? 'true' : 'false');
      if (iconMax) iconMax.textContent = isMax ? '🗗' : '🗖';
    }

    const floatingRestore = document.getElementById('btnFloatingRestoreBoard');
    if (floatingRestore) {
      floatingRestore.style.display = state === 'maximized' ? 'inline-flex' : 'none';
    }
  }

  static toggle3DRoomFullScreen() {
    if (typeof window !== 'undefined' && window._currentRenderer3D?.toggleFullScreen) {
      return window._currentRenderer3D.toggleFullScreen();
    }
  }

  static is3DRoomFullScreen() {
    if (typeof window !== 'undefined' && window._currentRenderer3D?.isFullScreen) {
      return window._currentRenderer3D.isFullScreen();
    }
    return false;
  }
}

